/**
 * Feb 2026 · Studio Booking hub — Story and Fame.
 * Single page with tabs: Bookings · Shoots · Studios & Spaces · Equipment · Crew · Packages.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";
import { Textarea } from "@/components/ui/textarea";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { Building2, Camera, Users, Package, Calendar, Clapperboard, Plus, Trash2, MapPin, Copy, QrCode, Flame, Link as LinkIcon, IndianRupee, Image as ImageIcon, Palette, ShieldOff, Pencil, GripVertical, X as XIcon, CalendarDays, ClipboardList, ExternalLink, ShoppingCart, Bell, Send, Search, Phone, MessageCircle, MessageSquare, ArrowUp, ArrowDown, ArrowUpDown, ArrowRight, Mail, Eye, RefreshCw, Clock,
  // Feb 2026 · Amenity picker curated list — one icon per common
  // studio amenity so admins tap once instead of typing keywords
  // from memory. Kept intentionally short (~30 icons) so the picker
  // fits inside a popover without scrolling on desktop.
  Wifi, Car, ParkingCircle, Coffee, Utensils, Snowflake, Fan, Tv, Music, Speaker, Mic, Wrench, Sofa, Shirt, ShowerHead, Toilet, Sparkles, DoorOpen, Baby, PawPrint, Accessibility, Zap, Battery, Lightbulb, Cctv, Lock, ShieldCheck, Wine, Refrigerator, Armchair, Bath, Bed,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import AdvancedTable from "@/components/table/AdvancedTable";
import CommentThread from "@/components/CommentThread";
import { AnalyticsTab } from "./studioBooking/Analytics";
import { AmenityIcon, AmenityListEditor, SpaceAmenitiesEditor } from "./studioBooking/Amenities";
import {
  MediaGallery, StudioGalleryEditor, GuidelinesUpload, ImageUpload,
  MediaBadge, STUDIO_GALLERY_MAX,
} from "./studioBooking/MediaUpload";
import { WidgetConfigEditor } from "./studioBooking/WidgetConfig";
import { StudioEmailSettings } from "./studioBooking/EmailSettings";
import { Bookings, BookingDetailDialog } from "./studioBooking/Bookings";
import { ManualBookingDialog, TimeSelect30 } from "./studioBooking/ManualBooking";
import { statusColor } from "./studioBooking/common";

const TABS = [
  { k: "myschedule", label: "My Schedule",   icon: CalendarDays,  view: "studio.crewview.access", manage: "studio.crewview.access", crew: true },
  { k: "crewbookings", label: "Crew Bookings", icon: ClipboardList, view: "studio.crewview.access", manage: "studio.crewview.access", crew: true },
  { k: "bookings",  label: "Bookings",  icon: Calendar,     view: "studio.bookings.view",  manage: "studio.bookings.manage" },
  { k: "calendar",  label: "Calendar",  icon: CalendarDays, view: "studio.bookings.view",  manage: "studio.bookings.manage" },
  { k: "shoots",    label: "Shoots",    icon: Clapperboard, view: "studio.shoots.view",    manage: "studio.shoots.manage" },
  { k: "heatmap",   label: "Heatmap",   icon: Flame,        view: "studio.bookings.view",  manage: "studio.bookings.manage" },
  { k: "studios",   label: "Studios",   icon: Building2,    view: "studio.studios.view",   manage: "studio.studios.manage" },
  { k: "equipment", label: "Equipment", icon: Camera,       view: "studio.equipment.view", manage: "studio.equipment.manage" },
  { k: "crew",      label: "Crew",      icon: Users,        view: "studio.crew.view",      manage: "studio.crew.manage" },
  { k: "packages",  label: "Packages",  icon: Package,      view: "studio.packages.view",  manage: "studio.packages.manage" },
  { k: "promos",    label: "Promos",    icon: IndianRupee,  view: "studio.settings.view",  manage: "studio.settings.manage" },
  { k: "services",  label: "Services",  icon: Package,      view: "studio.settings.view",  manage: "studio.settings.manage" },
  { k: "carts",     label: "Abandoned Carts", icon: ShoppingCart, view: "studio.bookings.view", manage: "studio.bookings.manage" },
  { k: "widget",    label: "Booking page", icon: Palette,   view: "studio.settings.view",  manage: "studio.settings.manage" },
  { k: "emails",    label: "Emails",       icon: Mail,       view: "studio.settings.view",  manage: "studio.settings.manage" },
  { k: "analytics", label: "Analytics",  icon: Flame,        view: "studio.bookings.view",  manage: "studio.bookings.view" },
];

export default function StudioBooking() {
  const { activeWorkspaceId: ws } = useAuth();
  const { can, ready } = useWorkspacePerms();
  const visibleTabs = TABS.filter((t) => !ready || can(t.view) || can(t.manage));
  const [tab, setTab] = useState(null);
  useEffect(() => {
    if (!tab && visibleTabs.length) setTab(visibleTabs[0].k);
  }, [tab, visibleTabs]);
  const current = TABS.find((t) => t.k === tab);
  const canManage = !!current && can(current.manage);

  // Feb 2026 · R268yyy · Prefer the custom booking-page slug for the
  // top-right "View public booking page" link when the admin has
  // configured one — the ID-based URL is what customers see if we
  // fall back, but the slug is what they'll actually share.
  const [publicSlug, setPublicSlug] = useState("");
  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/studio/widget-config`)
      .then((r) => setPublicSlug(r.data?.widget_config?.booking_slug || ""))
      .catch(() => setPublicSlug(""));
  }, [ws]);
  const publicBookingUrl = `/book/${publicSlug || ws}`;

  if (ready && visibleTabs.length === 0) {
    return (
      <div className="p-4 sm:p-6 md:px-10 flex flex-col items-center justify-center py-24 text-center" data-testid="studio-booking-page">
        <ShieldOff className="w-6 h-6 text-foreground/50 mb-2" />
        <h2 className="text-lg font-bold mb-1">Studio access restricted</h2>
        <p className="text-sm text-muted-foreground max-w-md">
          Your role doesn&rsquo;t include any Studio tabs. Ask a workspace admin to grant <code>studio.*</code> permissions in User Management → Roles.
        </p>
      </div>
    );
  }
  return (
    <div className="p-4 sm:p-6 md:px-10" data-testid="studio-booking-page">
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700/80 font-semibold mb-1">Studio</div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: `"Cormorant Garamond", Georgia, serif`, fontWeight: 500, letterSpacing: "-0.02em" }}>Booking control</h1>
          <p className="text-xs text-muted-foreground mt-1">Studios · Equipment · Shoot tracker · Public booking widget</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/app/studio/scan" data-testid="sb-scan-link"
                    title="Scan a guest's arrival QR at the front desk"
                    className="text-[11px] text-primary hover:bg-primary/5 inline-flex items-center gap-1.5 border border-primary/40 px-3.5 py-2 uppercase tracking-widest font-semibold transition-colors no-underline">
            <QrCode className="w-3.5 h-3.5" /> Scan tickets
          </Link>
          <a href={publicBookingUrl} target="_blank" rel="noopener noreferrer" data-testid="sb-public-widget-link"
             title={publicSlug ? `Custom booking URL: ${publicBookingUrl}` : `Default workspace booking URL: ${publicBookingUrl}`}
             className="text-[11px] text-primary hover:bg-primary/5 inline-flex items-center gap-1.5 border border-primary/40 px-3.5 py-2 uppercase tracking-widest font-semibold transition-colors">
            <MapPin className="w-3.5 h-3.5" /> View public booking page →
          </a>
        </div>
      </div>
      <div className="flex gap-1 border-b border-border mb-4 overflow-x-auto">
        {visibleTabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  data-testid={`sb-tab-${t.k}`}
                  className={`px-3 py-2 text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "myschedule"   && <CrewSchedule ws={ws} />}
      {tab === "crewbookings" && <CrewBookings ws={ws} />}
      {tab === "bookings"  && <Bookings  ws={ws} canManage={canManage} />}
      {tab === "calendar"  && <StudioCalendar ws={ws} canManage={canManage} />}
      {tab === "shoots"    && <Shoots    ws={ws} canManage={canManage} />}
      {tab === "heatmap"   && <Heatmap   ws={ws} />}
      {tab === "studios"   && <Studios   ws={ws} canManage={canManage} />}
      {tab === "equipment" && <Equipment ws={ws} canManage={canManage} />}
      {tab === "crew"      && <CrewRoles ws={ws} canManage={canManage} />}
      {tab === "packages"  && <Packages  ws={ws} canManage={canManage} />}
      {tab === "promos"    && <Promos    ws={ws} canManage={canManage} />}
      {tab === "services"  && <AdditionalServices ws={ws} canManage={canManage} />}
      {tab === "carts"     && <AbandonedCarts ws={ws} canManage={canManage} />}
      {tab === "widget"    && <WidgetConfigEditor ws={ws} canManage={canManage} />}
      {tab === "emails"    && <StudioEmailSettings ws={ws} canManage={canManage} />}
      {tab === "analytics" && <AnalyticsTab ws={ws} />}
    </div>
  );
}

// Media uploader group extracted to ./studioBooking/MediaUpload.jsx (Oct 2026).

function ResourceCrud({ ws, tag, label, columns, defaults, extraFields = null, detailHrefFor = null }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(defaults);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);   // Feb 2026 · null = create, else = row id
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio/${tag}s`);
      setRows(data[tag + "s"] || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws]);

  const openCreate = () => { setEditingId(null); setForm(defaults); setOpen(true); };
  const openEdit = (row) => {
    // Merge defaults with the row so unknown-to-defaults keys (like `media`) still round-trip.
    setEditingId(row.id);
    setForm({ ...defaults, ...row });
    setOpen(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/workspaces/${ws}/studio/${tag}s/${editingId}`, form);
        toast.success(`${label} updated`);
      } else {
        await api.post(`/workspaces/${ws}/studio/${tag}s`, form);
        toast.success(`${label} created`);
      }
      setOpen(false); setEditingId(null); setForm(defaults); await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}?`)) return;
    try {
      await api.delete(`/workspaces/${ws}/studio/${tag}s/${id}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div data-testid={`sb-${tag}-list`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{rows.length} {label}{rows.length === 1 ? "" : "s"}</div>
        <Button size="sm" className="rounded-none h-8" onClick={openCreate} data-testid={`sb-${tag}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add {label}
        </Button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>{columns.map((c) => <th key={c.k} className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px] text-muted-foreground whitespace-nowrap">{c.h}</th>)}
              <th className="w-20"></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/20">
                {columns.map((c) => <td key={c.k} className="px-2 py-1.5">{c.f ? c.f(r) : r[c.k]}</td>)}
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {detailHrefFor && (
                    <a href={detailHrefFor(r)} className="text-primary hover:text-primary/70 mr-2"
                        title="Open detail page" data-testid={`sb-${tag}-detail-${r.id}`}>
                      <ExternalLink className="w-3.5 h-3.5 inline"/>
                    </a>
                  )}
                  <button onClick={() => openEdit(r)} className="text-primary hover:text-primary/70 mr-2"
                          title="Edit" data-testid={`sb-${tag}-edit-${r.id}`}>
                    <Pencil className="w-3.5 h-3.5 inline"/>
                  </button>
                  <button onClick={() => del(r.id)} className="text-rose-600 hover:text-rose-800"
                          title="Delete" data-testid={`sb-${tag}-delete-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5 inline"/>
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length + 1} className="text-center py-6 text-muted-foreground italic">No {label.toLowerCase()}s yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setEditingId(null); } }}>
        <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`sb-${tag}-dialog`}>
          <DialogHeader><DialogTitle>{editingId ? `Edit ${label}` : `New ${label}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {columns.filter((c) => c.editable !== false).map((c) => (
              <div key={c.k}>
                <Label className="text-xs">{c.h}</Label>
                <Input value={form[c.k] ?? ""} onChange={(e) => setForm({ ...form, [c.k]: c.type === "number" ? Number(e.target.value) : e.target.value })}
                       type={c.type || "text"} className="rounded-none h-9" data-testid={`sb-${tag}-input-${c.k}`} />
                {c.help && <div className="text-[10px] text-muted-foreground mt-1 leading-snug">{c.help}</div>}
              </div>
            ))}
            {extraFields?.(form, setForm)}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setOpen(false); setEditingId(null); }}>Cancel</Button>
            <Button className="rounded-none" onClick={save} disabled={busy} data-testid={`sb-${tag}-save`}>
              {editingId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Studios({ ws }) {
  return (<>
    <ResourceCrud ws={ws} tag="studio" label="Studio"
      defaults={{ name: "", address: "", timezone: "Asia/Kolkata", active: true,
                    cover_image_url: "", logo_url: "", gallery_urls: [],
                    latitude: null, longitude: null,
                    directions_notes: "", phone: "", mobile: "", whatsapp: "",
                    website: "", customer_email_footer_html: "",
                    guidelines_file_url: "", guidelines_file_name: "",
                    payment_mode: "cashfree", upi_qr_url: "", upi_id: "", upi_payee_name: "",
                    map_url: "", amenities: [],
                    legal_entity_name: "", support_email: "" }}
      columns={[
        { k: "cover_image_url", h: "", editable: false, f: (r) => r.cover_image_url
              ? <img src={r.cover_image_url} alt="" className="w-12 h-8 object-cover border border-border" />
              : <div className="w-12 h-8 border border-border bg-muted/30" /> },
        { k: "name", h: "Name" }, { k: "address", h: "Address" },
        { k: "phone", h: "Phone" }, { k: "timezone", h: "TZ" },
        { k: "map", h: "Map", editable: false, f: (r) => {
            if (r.latitude != null && r.longitude != null) {
              const href = r.map_url || `https://www.google.com/maps/?q=${r.latitude},${r.longitude}`;
              return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">📍 Open</a>;
            }
            return <span className="text-muted-foreground">—</span>;
          } },
      ]}
      extraFields={(f, setF) => (
        <>
          <div>
            <Label className="text-xs">Cover image (widget hero · 16:9)</Label>
            <ImageUpload ws={ws} purpose="studio_cover"
                         value={f.cover_image_url}
                         onChange={(url) => setF({ ...f, cover_image_url: url })}
                         testid="sb-studio-cover"
                         zoomMode aspect={16 / 9} />
          </div>
          {/* Feb 2026 · R268kkk · Studio logo — small brand mark, shown
              on space tiles + slideshow + widget hero. Free aspect,
              rendered `object-fit: contain` so square OR wide logos
              both work. */}
          <div>
            <Label className="text-xs">Studio logo (square or wide brand mark)</Label>
            <ImageUpload ws={ws} purpose="logo"
                         value={f.logo_url}
                         onChange={(url) => setF({ ...f, logo_url: url })}
                         testid="sb-studio-logo" />
          </div>
          {/* Feb 2026 · R268kkk · Studio photo gallery — up to 12
              free-aspect photos. Displayed as a slideshow on the
              widget's space-picker step. */}
          <StudioGalleryEditor ws={ws}
                                value={f.gallery_urls || []}
                                onChange={(v) => setF({ ...f, gallery_urls: v })} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Latitude</Label>
              <Input type="number" step="0.000001" value={f.latitude ?? ""} onChange={(e) => setF({ ...f, latitude: e.target.value === "" ? null : Number(e.target.value) })}
                      className="rounded-none h-9" data-testid="sb-studio-input-lat" placeholder="e.g. 19.1136" />
            </div>
            <div>
              <Label className="text-xs">Longitude</Label>
              <Input type="number" step="0.000001" value={f.longitude ?? ""} onChange={(e) => setF({ ...f, longitude: e.target.value === "" ? null : Number(e.target.value) })}
                      className="rounded-none h-9" data-testid="sb-studio-input-lng" placeholder="e.g. 72.8697" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Phone (reception / display only)</Label>
            <Input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })}
                    className="rounded-none h-9" data-testid="sb-studio-input-phone" placeholder="+91…" />
          </div>
          {/* Feb 2026 · R268kkk · Split call vs WhatsApp routing so the
              widget shows two clear CTA buttons on every booking step. */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Mobile (Call button on widget)</Label>
              <Input value={f.mobile || ""} onChange={(e) => setF({ ...f, mobile: e.target.value })}
                      className="rounded-none h-9" data-testid="sb-studio-input-mobile"
                      placeholder="+91 98… (dials via tel:)" />
            </div>
            <div>
              <Label className="text-xs">WhatsApp (WhatsApp button)</Label>
              <Input value={f.whatsapp || ""} onChange={(e) => setF({ ...f, whatsapp: e.target.value })}
                      className="rounded-none h-9" data-testid="sb-studio-input-whatsapp"
                      placeholder="+91 98… (opens wa.me link)" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Studio website (shown on booking confirmation)</Label>
            <Input value={f.website || ""} onChange={(e) => setF({ ...f, website: e.target.value })}
                    className="rounded-none h-9" data-testid="sb-studio-input-website"
                    placeholder="https://storystudios.com" />
          </div>
          {/* Feb 2026 · CAN-SPAM / anti-spam compliance block. Rendered
              in the legal footer of every studio automated email: legal
              entity name, postal address (from `address` field above),
              phone (from `phone`), and a dedicated support email. The
              footer also carries Unsubscribe + Manage preferences
              links for marketing categories (reminders, abandoned
              cart). Transactional emails never show the unsubscribe
              link but DO show the address block. */}
          <div className="space-y-2 border-l-2 border-amber-200 pl-3 bg-amber-50/30 p-3"
                 data-testid="sb-studio-compliance-block">
            <Label className="text-[11px] font-semibold uppercase tracking-widest text-amber-900">
              Email legal footer · CAN-SPAM compliance
            </Label>
            <p className="text-[11px] text-muted-foreground leading-snug">
              These fields appear in the legal footer of every automated email from this
              studio so you stay compliant with Gmail / Yahoo bulk-sender rules and
              CAN-SPAM / anti-spam regulations. Postal address comes from the Address
              field above; phone comes from the Phone field above.
            </p>
            <div>
              <Label className="text-xs">Legal entity name (optional)</Label>
              <Input value={f.legal_entity_name || ""}
                       onChange={(e) => setF({ ...f, legal_entity_name: e.target.value })}
                       className="rounded-none h-9" data-testid="sb-studio-input-legal-entity"
                       placeholder={`e.g. ${f.name || "Story & Fame Studios Pvt Ltd"} — defaults to Studio name`} />
            </div>
            <div>
              <Label className="text-xs">Support email (for inbox replies & customer questions)</Label>
              <Input type="email" value={f.support_email || ""}
                       onChange={(e) => setF({ ...f, support_email: e.target.value })}
                       className="rounded-none h-9" data-testid="sb-studio-input-support-email"
                       placeholder="hello@storystudios.com" />
            </div>
          </div>
          {/* Oct 2026 · Rich-text footer appended to the customer-facing
              booking email. Admins use this to pitch add-on services,
              link to Instagram / portfolio, or share pre-shoot
              checklists. Admin notification emails never carry it. */}
          <div className="space-y-1" data-testid="sb-studio-customer-email-footer">
            <Label className="text-xs">Customer email upsell / footer</Label>
            <p className="text-[11px] text-muted-foreground">
              Appended ONLY to the customer's branded booking email (not the admin notification).
              Perfect for a thank-you note, "Book again" link, or Instagram shout-out.
            </p>
            <RichTextEditor
              value={f.customer_email_footer_html || ""}
              onChange={(html) => setF({ ...f, customer_email_footer_html: html })}
              placeholder="Thanks again — follow us on Instagram @storystudios for new drops and shoot ideas…"
              minHeight={120}
              workspaceId={ws}
              testid="sb-studio-customer-email-footer-editor"
            />
          </div>
          {/* Feb 2026 · R268qqq · Studio guidelines file — attached to
              the booking confirmation email AND offered as a download
              link on the confirmation screen so customers get shoot
              day rules, parking map, wardrobe notes in one place. */}
          <GuidelinesUpload ws={ws}
              url={f.guidelines_file_url || ""}
              name={f.guidelines_file_name || ""}
              onChange={(url, name) => setF({ ...f, guidelines_file_url: url,
                                               guidelines_file_name: name })} />
          {/* R268uuu · Feb 2026 · Payment mode toggle.
              cashfree → the widget redirects to Cashfree checkout.
              upi_qr  → widget shows a UPI QR + proof-upload form;
                         admin manually confirms.
              offline → widget shows "our team will reach out". */}
          <div className="border border-border bg-muted/10 p-3 space-y-3" data-testid="sb-studio-payment-mode">
            <Label className="text-xs">Payment collection method</Label>
            <select value={f.payment_mode || "cashfree"}
                     onChange={(e) => setF({ ...f, payment_mode: e.target.value })}
                     className="w-full h-9 border border-input bg-background px-2 text-xs"
                     data-testid="sb-studio-payment-mode-select">
              <option value="cashfree">Online (Cashfree checkout — cards, UPI apps, wallets)</option>
              <option value="upi_qr">Direct UPI QR (customer pays, uploads proof, you verify)</option>
              <option value="offline">Offline (team reaches out to collect payment)</option>
            </select>
            {f.payment_mode === "upi_qr" && (
              <div className="space-y-2 pt-2 border-t border-border/60">
                <div>
                  <Label className="text-xs">UPI QR image (shown to customer at checkout)</Label>
                  <ImageUpload ws={ws} purpose="studio_cover"
                                 value={f.upi_qr_url || ""}
                                 onChange={(url) => setF({ ...f, upi_qr_url: url })}
                                 testid="sb-studio-upi-qr" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">UPI ID (fallback if QR fails)</Label>
                    <Input value={f.upi_id || ""}
                            onChange={(e) => setF({ ...f, upi_id: e.target.value })}
                            className="rounded-none h-9 font-mono"
                            data-testid="sb-studio-upi-id"
                            placeholder="storystudio@ybl" />
                  </div>
                  <div>
                    <Label className="text-xs">Payee name (as shown to customer)</Label>
                    <Input value={f.upi_payee_name || ""}
                            onChange={(e) => setF({ ...f, upi_payee_name: e.target.value })}
                            className="rounded-none h-9"
                            data-testid="sb-studio-upi-payee"
                            placeholder="Story and Fame Studio LLP" />
                  </div>
                </div>
                <div className="text-[11px] text-amber-700 bg-amber-50 border-l-2 border-amber-500 p-2">
                  <b>How it works:</b> customer scans your QR, pays, then uploads a screenshot or txn ID. Booking stays pending until you mark it confirmed in the Bookings tab.
                </div>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Custom map URL (optional)</Label>
            <Input value={f.map_url || ""} onChange={(e) => setF({ ...f, map_url: e.target.value })}
                    className="rounded-none h-9" data-testid="sb-studio-input-mapurl" placeholder="Overrides the lat/lng link" />
          </div>
          <div>
            <Label className="text-xs">Directions notes</Label>
            <Textarea value={f.directions_notes || ""} onChange={(e) => setF({ ...f, directions_notes: e.target.value })}
                       rows={3} className="rounded-none text-xs" data-testid="sb-studio-input-directions"
                       placeholder="Enter through gate B · parking behind the building · take elevator to floor 3" />
          </div>
          {/* Feb 2026 · Studio-level amenities. Every space under this
              studio inherits them by default. Individual spaces can
              opt to override or extend from the space editor below. */}
          <AmenityListEditor value={f.amenities} onChange={(v) => setF({ ...f, amenities: v })}
                              level="studio" testid="sb-studio-amenities" />
          {/* R268bbb · Feb 2026 · Per-studio Cashfree keys — overrides
              the workspace default AND any per-space keys. Precedence
              (highest → lowest): studio > space > workspace. Blank
              here falls back to the space or workspace config. */}
          {f.id && <StudioCashfreeInline ws={ws} studioId={f.id} />}
        </>
      )}
    />
    <div className="mt-6">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Spaces (bookable sub-rooms)</div>
      <SpacesInner ws={ws} />
    </div>
  </>);
}

/* R268bbb · Feb 2026 · Compact inline Cashfree panel embedded in the
   studio editor row. Reuses the same encrypted-persistence pattern as
   the per-space keys — leave blank to keep the previous value; leave
   `Use per-studio keys` off entirely to fall back to space → workspace.

   Deliberately terse: full documentation of Cashfree modes lives on
   the space-level page (`StudioSpaceDetail.jsx`). The heavy lifting
   is server-side (`get_cfg` precedence) — this UI just captures three
   fields + an enable toggle. */
function StudioCashfreeInline({ ws, studioId }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({
    enabled: false, mode: "TEST",
    client_id: "", client_secret: "", webhook_secret: "",
  });
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const reload = () => {
    api.get(`/workspaces/${ws}/studio/studios/${studioId}/cashfree`)
      .then((r) => {
        setState(r.data);
        setForm((f) => ({ ...f, enabled: !!r.data.enabled, mode: r.data.mode || "TEST" }));
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!ws || !studioId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, studioId]);

  const save = async () => {
    setBusy(true);
    try {
      // Only send fields with values so we don't overwrite existing
      // encrypted secrets with blanks.
      const body = { enabled: form.enabled, mode: form.mode };
      if (form.client_id.trim()) body.client_id = form.client_id.trim();
      if (form.client_secret.trim()) body.client_secret = form.client_secret.trim();
      if (form.webhook_secret.trim()) body.webhook_secret = form.webhook_secret.trim();
      const { data } = await api.put(
        `/workspaces/${ws}/studio/studios/${studioId}/cashfree`, body);
      setState(data);
      setForm((f) => ({ ...f, client_id: "", client_secret: "", webhook_secret: "" }));
      setDirty(false);
      toast.success("Studio Cashfree keys saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  // Feb 2026 · Effective-config chip. Backend returns `effective_level`
  // (studio | space | workspace | null) + `effective_label` + masked
  // client ID + mode so admins can see *which merchant will collect*
  // for this studio — critical before flipping to LIVE.
  const eff = state || {};
  const usingStudio = eff.effective_level === "studio";
  const chipClass = usingStudio
    ? "bg-emerald-50 border-emerald-300 text-emerald-800"
    : "bg-amber-50 border-amber-300 text-amber-800";
  const chipIcon = usingStudio ? "✓" : "↳";
  const chipTitle = usingStudio
    ? "Using this studio's own Cashfree keys"
    : `Falling back to ${eff.effective_level || "workspace"} — set per-studio keys below to override`;

  return (
    <div className="border-t border-border pt-3 mt-2 space-y-2" data-testid="sb-studio-cashfree-inline">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] font-semibold uppercase tracking-widest">
          Cashfree keys · this studio only
        </Label>
        <label className="text-[11px] inline-flex items-center gap-2">
          <input type="checkbox" checked={form.enabled}
                 onChange={(e) => update({ enabled: e.target.checked })}
                 data-testid="sb-studio-cf-enabled" />
          <span>Use per-studio keys</span>
        </label>
      </div>
      <div className="text-[10px] text-muted-foreground -mt-1">
        Precedence at checkout: <b>studio</b> → space → workspace. Blank keys
        keep the previous value; disable the toggle to fall back entirely.
      </div>
      {/* Effective merchant chip — visible whether or not this studio is
          the one providing the keys. Shows admins the actual merchant
          that will collect payment for bookings on this studio. */}
      {eff.effective_level ? (
        <div
          data-testid="sb-studio-cf-effective-chip"
          title={chipTitle}
          className={`inline-flex items-center gap-2 rounded-none border px-2 py-1 text-[11px] ${chipClass}`}
        >
          <span className="font-semibold">{chipIcon}</span>
          <span>
            {usingStudio ? "Collecting via " : "Falling back to "}
            <b>{eff.effective_label}</b>
            {" "}({eff.effective_level})
          </span>
          <span className="opacity-60">·</span>
          <span className="font-mono">{eff.effective_client_id_masked || "no client id"}</span>
          <span className="opacity-60">·</span>
          <span className={`font-semibold ${eff.effective_mode === "LIVE" ? "text-red-600" : ""}`}>
            {eff.effective_mode || "TEST"}
          </span>
        </div>
      ) : (
        <div
          data-testid="sb-studio-cf-effective-chip"
          className="inline-flex items-center gap-2 rounded-none border border-red-300 bg-red-50 text-red-700 px-2 py-1 text-[11px]"
        >
          <span className="font-semibold">!</span>
          <span>No Cashfree merchant configured — bookings on this studio can't collect payment.</span>
        </div>
      )}
      {form.enabled && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.mode} onChange={(e) => update({ mode: e.target.value })}
                    className="h-9 border border-border bg-background text-xs rounded-none px-2"
                    data-testid="sb-studio-cf-mode">
              <option value="TEST">Test (sandbox)</option>
              <option value="LIVE">Live (production)</option>
            </select>
            <div className="flex items-center gap-2 text-[11px]">
              {state?.has_client_id
                ? <span className="text-emerald-600">✓ Client ID saved</span>
                : <span className="text-muted-foreground">No client ID yet</span>}
              {state?.has_client_secret
                ? <span className="text-emerald-600">· ✓ Secret saved</span>
                : null}
            </div>
          </div>
          <Input value={form.client_id} onChange={(e) => update({ client_id: e.target.value })}
                 placeholder="Client ID (leave blank to keep existing)"
                 className="rounded-none h-9 text-xs" data-testid="sb-studio-cf-client-id" />
          <Input type="password" value={form.client_secret}
                 onChange={(e) => update({ client_secret: e.target.value })}
                 placeholder="Client secret (leave blank to keep existing)"
                 className="rounded-none h-9 text-xs" data-testid="sb-studio-cf-client-secret" />
          <Input type="password" value={form.webhook_secret}
                 onChange={(e) => update({ webhook_secret: e.target.value })}
                 placeholder="Webhook secret (required before going LIVE)"
                 className="rounded-none h-9 text-xs" data-testid="sb-studio-cf-webhook-secret" />
        </>
      )}
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={save} disabled={busy || !dirty}
                className="rounded-none h-8 text-xs" data-testid="sb-studio-cf-save">
          {busy ? "Saving…" : "Save Cashfree keys"}
        </Button>
      </div>
    </div>
  );
}


/* Feb 2026 · Reusable amenity-list editor used by both the studio and
   space forms. Each row is `{ id, name, icon, description }`. `icon`
   is a lucide-react icon key (e.g. "wifi", "car", "door-open"). Left
   blank if the customer just wants a bare list. Keys stay stable
   across renders (uses `id`), preventing the input focus-jump that
   plagues index-keyed dynamic form lists. */
// Amenity icons + editors extracted to ./studioBooking/Amenities.jsx (Oct 2026).


function SpacesInner({ ws }) {
  const [studios, setStudios] = useState([]);
  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/studio/studios`).then((r) => setStudios(r.data.studios || [])).catch(() => {});
  }, [ws]);
  const studiosById = useMemo(() => Object.fromEntries((studios || []).map((s) => [s.id, s])), [studios]);
  return (
    <ResourceCrud ws={ws} tag="space" label="Space"
      detailHrefFor={(r) => `/app/studio/spaces/${r.id}`}
      defaults={{ studio_id: studios[0]?.id || "", name: "", slot_mode: "both", hourly_rate: 0, halfday_rate: 0, fullday_rate: 0, peak_multiplier: 1.25, buffer_min: 30, damage_deposit: 0, collect_deposit_upfront: false, active: true, description: "", media: [], amenities_mode: "inherit", amenities: [], blocks_entire_studio: false }}
      columns={[
        { k: "name", h: "Name", f: (r) => (
          <span className="inline-flex items-center gap-1.5">
            {r.name}
            {r.blocks_entire_studio && (
              <span className="text-[9px] px-1 py-0.5 border border-amber-400 bg-amber-50 text-amber-800 uppercase tracking-widest font-semibold"
                    title="Booking this space blocks every other space in the same studio for that slot"
                    data-testid={`sb-space-shared-badge-${r.id}`}>
                Shared
              </span>
            )}
          </span>
        ) },
        { k: "hourly_rate", h: "Hourly (₹)", type: "number" },
        { k: "halfday_rate", h: "½-day (₹)", type: "number" },
        { k: "fullday_rate", h: "Full-day (₹)", type: "number" },
        { k: "peak_multiplier", h: "Peak ×", type: "number",
          help: "Weekend / peak-day price multiplier. e.g. 1.25 means every rate above is bumped by 25% on peak days (Sat & Sun by default). Set to 1 to disable." },
        // Oct 2026 · Clean-up buffer between bookings. Expose on the
        // columns list so admins can see / edit the value at a glance
        // without opening the full edit panel.
        { k: "buffer_min", h: "Buffer (min)", type: "number",
          help: "Cleanup buffer blocked after every booking so back-to-back shoots don't clash." },
        // Feb 2026 · R288 · Refundable deposit on the space. Displayed
        // as a refundable line on the public quote. Toggle below in
        // extra fields decides whether it's charged upfront.
        { k: "damage_deposit", h: "Deposit (₹)", type: "number",
          help: "Refundable damage deposit held per booking. Returned after the shoot if nothing was damaged. Use the toggle below to decide if it's charged upfront with the booking or collected separately on-site." },
        { k: "media", h: "Media", editable: false, f: (r) => <MediaBadge value={r.media} /> },
      ]}
      extraFields={(f, setF) => (
        <>
          <div>
            <Label className="text-xs">Studio</Label>
            <select value={f.studio_id || ""} onChange={(e) => setF({ ...f, studio_id: e.target.value })}
                    className="w-full h-9 border border-border bg-background text-xs rounded-none px-2" data-testid="sb-space-input-studio_id">
              <option value="">Select…</option>
              {studios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })}
                       rows={2} className="rounded-none text-xs" data-testid="sb-space-input-description" />
          </div>
          <AvailabilityEditor value={f} onChange={(patch) => setF({ ...f, ...patch })} testid="sb-space-avail" />
          {/* Feb 2026 · R268yyy · Shared-studio block. Turn ON when
              multiple crews on the same premises would clash — e.g.
              open-plan lofts, single-toilet studios, shared makeup
              rooms. When set, any booking on this space auto-blocks
              every other space in the same parent studio for the same
              slot (and vice versa). */}
          <div className="border border-border p-3 space-y-1" data-testid="sb-space-shared-block-wrap">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="mt-0.5"
                      checked={!!f.blocks_entire_studio}
                      onChange={(e) => setF({ ...f, blocks_entire_studio: e.target.checked })}
                      data-testid="sb-space-input-blocks_entire_studio" />
              <div>
                <div className="text-xs font-semibold">Booking this space blocks the whole studio</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  Use for open-plan or shared-facility studios where two crews on-site would clash. Any active booking on this space also freezes every other space in the same studio for that slot.
                </div>
              </div>
            </label>
          </div>
          {/* Feb 2026 · R288 · Deposit collection mode. Only meaningful
              when `damage_deposit > 0`. OFF (default) = refundable
              deposit is shown as a line on the quote but NOT added to
              the Pay Now amount; studio collects it separately on-site.
              ON = deposit is added to the deposit_due so Cashfree / UPI
              charges the full amount upfront. */}
          <div className="border border-border p-3 space-y-1" data-testid="sb-space-deposit-upfront-wrap">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="mt-0.5"
                      checked={!!f.collect_deposit_upfront}
                      onChange={(e) => setF({ ...f, collect_deposit_upfront: e.target.checked })}
                      disabled={!Number(f.damage_deposit)}
                      data-testid="sb-space-input-collect_deposit_upfront" />
              <div>
                <div className="text-xs font-semibold">Collect refundable deposit upfront</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  When ON, the deposit above is added to the amount the customer pays at booking time (and refunded after the shoot). When OFF, the deposit is only shown as a refundable line on the quote and your team collects it on-site. Needs a non-zero Deposit value above to be enabled.
                </div>
              </div>
            </label>
          </div>
          <div>
            <Label className="text-xs">Photos &amp; videos</Label>
            <MediaGallery ws={ws} value={f.media || []}
                           onChange={(m) => setF({ ...f, media: m })}
                           purpose="space" testid="sb-space-media" />
          </div>
          {/* Feb 2026 · Amenity inheritance. Default `inherit` uses the
              parent studio's list as-is. `override` ignores the studio.
              `extend` merges (studio first, dedupe by name).  Bookings
              widget shows the effective list on the space tile. */}
          <SpaceAmenitiesEditor
            value={f}
            onChange={(patch) => setF({ ...f, ...patch })}
            studioAmenities={studiosById[f.studio_id]?.amenities || []}
            testid="sb-space-amenities"
          />
        </>
      )}
    />
  );
}

// MediaBadge extracted to ./studioBooking/MediaUpload.jsx (Oct 2026).

function Equipment({ ws }) {
  return (<>
    <ResourceCrud ws={ws} tag="equipment-category" label="Category"
      defaults={{ name: "", icon: "📷", color: "#EA5934" }}
      columns={[{ k: "icon", h: "" }, { k: "name", h: "Name" }, { k: "color", h: "Color" }]} />
    <div className="mt-6">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Equipment items (serialized)</div>
      <EquipmentInner ws={ws} />
    </div>
  </>);
}

function EquipmentInner({ ws }) {
  const [cats, setCats] = useState([]);
  const [qrItem, setQrItem] = useState(null);
  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/studio/equipment-categorys`).then((r) => setCats(r.data["equipment-categorys"] || [])).catch(() => {});
  }, [ws]);
  return (
    <>
      <ResourceCrud ws={ws} tag="equipment-item" label="Item"
        defaults={{ category_id: cats[0]?.id || "", name: "", serial_no: "", hourly_rate: 0, daily_rate: 0, damage_deposit: 0, replacement_value: 0, condition: "good", active: true, notes: "", media: [] }}
        columns={[
          { k: "name", h: "Name" }, { k: "serial_no", h: "Serial" },
          { k: "hourly_rate", h: "₹/hr", type: "number" },
          { k: "daily_rate", h: "₹/day", type: "number" },
          { k: "damage_deposit", h: "Deposit", type: "number",
            help: "Refundable damage deposit per rental. Shown as a refundable line on the customer's quote and held until the item is returned in good condition." },
          { k: "condition", h: "Condition" },
          { k: "media", h: "Media", editable: false, f: (r) => <MediaBadge value={r.media} /> },
          { k: "qr", h: "", editable: false, f: (r) => (
            <button onClick={() => setQrItem(r)} className="text-primary hover:text-primary/70" data-testid={`sb-equipment-qr-${r.id}`}>
              <QrCode className="w-4 h-4" />
            </button>
          ) },
        ]}
        extraFields={(f, setF) => (
          <>
            <div>
              <Label className="text-xs">Category</Label>
              <select value={f.category_id || ""} onChange={(e) => setF({ ...f, category_id: e.target.value })}
                      className="w-full h-9 border border-border bg-background text-xs rounded-none px-2" data-testid="sb-equipment-item-input-category_id">
                <option value="">Select…</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })}
                         rows={2} className="rounded-none text-xs" data-testid="sb-equipment-item-input-notes" />
            </div>
            <AvailabilityEditor value={f} onChange={(patch) => setF({ ...f, ...patch })} testid="sb-equipment-avail" />
            <div>
              <Label className="text-xs">Photos &amp; videos</Label>
              <MediaGallery ws={ws} value={f.media || []}
                             onChange={(m) => setF({ ...f, media: m })}
                             purpose="equipment" testid="sb-equipment-media" />
            </div>
          </>
        )}
      />
      <QrDialog ws={ws} item={qrItem} onClose={() => setQrItem(null)} />
    </>
  );
}

function QrDialog({ ws, item, onClose }) {
  if (!item) return null;
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/v1/workspaces/${ws}/studio/equipment-items/${item.id}/qr.png?size=10`;
  const scanUrl = `${window.location.origin}/app/studio/scan/${item.id}`;
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-sm" data-testid="sb-qr-dialog">
        <DialogHeader><DialogTitle>QR tag · {item.name}</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <img src={src} alt="Equipment QR" className="w-64 h-64 border border-border" />
          <div className="text-[10px] text-muted-foreground font-mono break-all text-center">{scanUrl}</div>
          <div className="flex gap-2 w-full">
            <Button variant="outline" className="rounded-none flex-1" onClick={() => window.open(src, "_blank")} data-testid="sb-qr-download">
              Print / download
            </Button>
            <Button variant="outline" className="rounded-none flex-1" onClick={() => { navigator.clipboard.writeText(scanUrl); toast.success("Scan URL copied"); }} data-testid="sb-qr-copy">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy URL
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CrewRoles({ ws }) {
  return <ResourceCrud ws={ws} tag="crew-role" label="Role"
    defaults={{ name: "", hourly_rate: 0, daily_rate: 0, description: "", active: true, photo_url: "", media: [] }}
    columns={[
      { k: "photo_url", h: "", editable: false, f: (r) => {
        const _abs = (u) => (u && u.startsWith("/api/")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
        return r.photo_url
          ? <img src={_abs(r.photo_url)} alt="" className="w-8 h-8 rounded-full object-cover border border-border" />
          : <div className="w-8 h-8 rounded-full border border-border bg-muted/30 flex items-center justify-center text-[10px] text-muted-foreground">?</div>;
      } },
      { k: "name", h: "Role" },
      { k: "hourly_rate", h: "₹/hr", type: "number" },
      { k: "daily_rate", h: "₹/day", type: "number" },
      { k: "description", h: "Description" },
      { k: "media", h: "Portfolio", editable: false, f: (r) => <MediaBadge value={r.media} /> },
    ]}
    extraFields={(f, setF) => (
      <>
        <div>
          <Label className="text-xs">Portrait / headshot</Label>
          <ImageUpload ws={ws} purpose="crew" value={f.photo_url}
                        onChange={(url) => setF({ ...f, photo_url: url })}
                        testid="sb-crew-photo" cropMode aspect={1} />
        </div>
        <div>
          <Label className="text-xs">Portfolio (photos &amp; videos)</Label>
          <MediaGallery ws={ws} value={f.media || []}
                         onChange={(m) => setF({ ...f, media: m })}
                         purpose="crew" testid="sb-crew-media" />
        </div>
      </>
    )} />;
}

function Packages({ ws }) {
  return <ResourceCrud ws={ws} tag="package" label="Package"
    defaults={{ name: "", description: "", price: 0, duration_hours: 4, includes_editing: false, active: true }}
    columns={[
      { k: "name", h: "Name" },
      { k: "price", h: "₹", type: "number" },
      { k: "duration_hours", h: "Hours", type: "number" },
      { k: "description", h: "Description" },
    ]} />;
}


// Feb 2026 · Promo codes for studio bookings — timebound offers,
// special rates and discount codes. Scoping is optional: leave
// space_ids / package_ids empty to apply workspace-wide. `auto_apply`
// promos are applied silently by the pricing engine when no explicit
// code is entered (useful for site-wide launch discounts).
function Promos({ ws, canManage }) {
  const [rows, setRows] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | promo object | 'new'
  const [dropoff, setDropoff] = useState(null);
  // Feb 2026 · Countdown-urgency analytics loaded from the new
  // `/promos/analytics` endpoint. Rendered as a bar chart next to the
  // dropoff panel so admins can see whether sub-24h-urgency drives
  // more bookings than relaxed windows.
  const [analytics, setAnalytics] = useState(null);
  const load = async () => {
    try {
      const [pr, sp, pk, dr, an] = await Promise.all([
        api.get(`/workspaces/${ws}/studio/promos`),
        api.get(`/workspaces/${ws}/studio/spaces`),
        api.get(`/workspaces/${ws}/studio/packages`),
        api.get(`/workspaces/${ws}/studio/funnel/dropoff`),
        api.get(`/workspaces/${ws}/studio/promos/analytics?days=90`),
      ]);
      setRows(pr.data.promos || []);
      setSpaces(sp.data.spaces || []);
      setPackages(pk.data.packages || []);
      setDropoff(dr.data || null);
      setAnalytics(an.data || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws]);

  const del = async (p) => {
    if (!window.confirm(`Delete promo ${p.code}?`)) return;
    try {
      await api.delete(`/workspaces/${ws}/studio/promos/${p.id}`);
      toast.success("Promo deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const now = new Date().toISOString();
  const statusOf = (p) => {
    if (!p.active) return { label: "Paused", cls: "bg-neutral-100 text-neutral-600 border-neutral-300" };
    if (p.starts_at && now < p.starts_at) return { label: "Scheduled", cls: "bg-blue-50 text-blue-700 border-blue-300" };
    if (p.ends_at && now > p.ends_at) return { label: "Expired", cls: "bg-rose-50 text-rose-700 border-rose-300" };
    if (p.max_uses && (p.usage_count || 0) >= p.max_uses) return { label: "Depleted", cls: "bg-rose-50 text-rose-700 border-rose-300" };
    return { label: "Live", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" };
  };

  return (
    <div data-testid="sb-promos">
      {/* Funnel drop-off — spots the biggest friction between view and paid */}
      <FunnelDropoffPanel dropoff={dropoff} />
      {/* Feb 2026 · Countdown-urgency chart — booking count per
          hours-remaining bucket. Admins see at a glance whether
          sub-24h urgency actually drives more bookings than relaxed
          windows (= does the countdown ribbon work?). */}
      <PromoCountdownAnalytics analytics={analytics} />
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-bold">Promo codes</h2>
          <p className="text-[11px] text-muted-foreground">Timebound offers, special rates, and discount codes for studio bookings.</p>
        </div>
        {canManage && (
          <Button size="sm" className="rounded-none" onClick={() => setEditing("new")} data-testid="sb-promo-new">
            <Plus className="w-3.5 h-3.5 mr-1" /> New promo
          </Button>
        )}
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              {["Code", "Discount", "Window", "Scope", "Funnel", "Status", ""].map((h, i) =>
                <th key={i} className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px] text-muted-foreground whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const s = statusOf(p);
              const disc = p.type === "percent" ? `${p.value}% off` : `₹${p.value} off`;
              const win = [p.starts_at?.slice(0, 10), p.ends_at?.slice(0, 10)].filter(Boolean).join(" → ") || "Always";
              const scope = (p.space_ids?.length ? `${p.space_ids.length} space${p.space_ids.length > 1 ? "s" : ""}` : "All spaces")
                          + (p.package_ids?.length ? ` · ${p.package_ids.length} pkg` : "");
              const usage = p.max_uses ? `${p.usage_count || 0} / ${p.max_uses}` : (p.usage_count || 0);
              const f = p.funnel || {};
              // Views = clicks that actually landed on the widget. Paid = converted.
              const views = f.view || 0;
              const paid  = f.step_paid || 0;
              const cvr   = views > 0 ? Math.round((paid / views) * 1000) / 10 : null;
              return (
                <tr key={p.id} className="border-t border-border" data-testid={`sb-promo-row-${p.id}`}>
                  <td className="px-2 py-1.5 align-top">
                    <div className="font-mono font-bold">{p.code}
                      {p.auto_apply && <span className="text-[9px] uppercase tracking-widest ml-1 bg-primary/10 text-primary px-1">Auto</span>}
                    </div>
                    {p.name && <div className="text-[11px] text-muted-foreground truncate max-w-[180px]">{p.name}</div>}
                  </td>
                  <td className="px-2 py-1.5 font-mono align-top">{disc}</td>
                  <td className="px-2 py-1.5 text-[11px] align-top">{win}{p.min_hours ? <div className="text-[10px] text-muted-foreground">min {p.min_hours}h</div> : null}</td>
                  <td className="px-2 py-1.5 text-[11px] align-top">{scope}</td>
                  <td className="px-2 py-1.5 text-[11px] align-top" data-testid={`sb-promo-funnel-${p.id}`}>
                    <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 min-w-[130px]">
                      <span className="text-muted-foreground">Views</span>
                      <span className="font-mono col-span-2">{views}</span>
                      <span className="text-muted-foreground">Uses</span>
                      <span className="font-mono col-span-2">{usage}</span>
                      <span className="text-muted-foreground">CVR</span>
                      <span className={`font-mono col-span-2 ${cvr != null && cvr >= 10 ? "text-emerald-700 font-semibold" : ""}`}>
                        {cvr == null ? "—" : `${cvr}%`}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 align-top"><span className={`text-[10px] px-1.5 py-0.5 border ${s.cls}`}>{s.label}</span></td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap align-top">
                    {canManage && (
                      <div className="flex flex-col gap-1 items-end">
                        <ShareLinkButton ws={ws} promo={p} />
                        <div>
                          <button onClick={() => setEditing(p)} className="text-primary hover:underline text-[11px] mr-2" data-testid={`sb-promo-edit-${p.id}`}>Edit</button>
                          <button onClick={() => del(p)} className="text-rose-600 hover:underline text-[11px]" data-testid={`sb-promo-delete-${p.id}`}>Delete</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground italic">No promo codes yet. Create one to start running offers.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <PromoDialog ws={ws} promo={editing === "new" ? null : editing}
                     spaces={spaces} packages={packages}
                     onClose={() => setEditing(null)}
                     onDone={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

// Feb 2026 · Funnel drop-off panel — one horizontal stepper per funnel
// (workspace overall + each promo with at least one landing). Each step
// shows the count and the % drop from the previous step; the biggest
// drop-off is highlighted in rose so admins can see at a glance where
// customers bail. Negative drops (users skipped a step) render as "—".
// Feb 2026 · Countdown-urgency analytics — bar chart of bookings
// per hours-remaining bucket. The question we're answering: "did the
// countdown ribbon on the booking widget actually drive conversion in
// the last 24 hours before an offer expired?" If the <24h bar
// outperforms the 48h/72h bars by booking count or revenue, the
// urgency UX is paying off.
function PromoCountdownAnalytics({ analytics }) {
  const series = analytics?.series || [];
  const total = analytics?.total_bookings || 0;
  const [metric, setMetric] = useState("bookings"); // "bookings" | "revenue"
  if (!series.length) return null;
  const anyBookings = series.some((s) => s.bookings > 0);
  if (!anyBookings) {
    return (
      <Card className="mb-3 rounded-none border-dashed">
        <CardContent className="p-4" data-testid="sb-promo-analytics-empty">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Countdown urgency · last {analytics?.days || 90} days
            </h3>
            <span className="text-[10px] text-muted-foreground">No promo bookings yet</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Once customers start booking with time-limited promo codes, this chart will show
            whether the "Ends in …" countdown ribbon actually drives last-minute conversions.
          </p>
        </CardContent>
      </Card>
    );
  }
  // Highlight the leading bucket in rose so the admin spots it instantly.
  const peakIdx = series.reduce((best, s, i, arr) =>
    (s[metric] > (arr[best]?.[metric] || 0) ? i : best), 0);
  return (
    <Card className="mb-3 rounded-none" data-testid="sb-promo-analytics">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest">
              Countdown urgency · last {analytics?.days || 90} days
            </h3>
            <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
              Bookings grouped by how much time was left on the promo when the customer booked.
              Winning bucket is highlighted — if the <b>&lt;24h</b> bar leads, the countdown ribbon is converting.
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button type="button" size="sm" variant={metric === "bookings" ? "default" : "outline"}
                     className="rounded-none h-7 text-[10px] uppercase tracking-wider"
                     onClick={() => setMetric("bookings")}
                     data-testid="sb-promo-analytics-metric-bookings">
              Bookings
            </Button>
            <Button type="button" size="sm" variant={metric === "revenue" ? "default" : "outline"}
                     className="rounded-none h-7 text-[10px] uppercase tracking-wider"
                     onClick={() => setMetric("revenue")}
                     data-testid="sb-promo-analytics-metric-revenue">
              Revenue
            </Button>
          </div>
        </div>
        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#F0E7D4" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#5C4A38" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#5C4A38" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: "#F7EFD9" }}
                contentStyle={{ fontSize: 11, borderRadius: 0, border: "1px solid #ECD9B2", background: "#FFF" }}
                formatter={(v) => metric === "revenue" ? [`₹${Number(v).toFixed(0)}`, "Revenue"] : [v, "Bookings"]}
              />
              <Bar dataKey={metric} radius={[0, 0, 0, 0]}>
                {series.map((s, i) => (
                  <Cell key={s.bucket}
                         fill={s.bucket === "0_24h" ? "#C8302E"
                                : s.bucket === "24_48h" ? "#D9A851"
                                : s.bucket === "48_72h" ? "#B98B33"
                                : s.bucket === "72h_plus" ? "#8A7A66"
                                : "#C9B38A"}
                         opacity={i === peakIdx ? 1 : 0.72} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>Total promo-attributed bookings in window: <b className="text-foreground">{total}</b></span>
          <span>Leading window: <b style={{ color: series[peakIdx]?.bucket === "0_24h" ? "#C8302E" : "#241A11" }}>
            {series[peakIdx]?.label}
          </b></span>
        </div>
      </CardContent>
    </Card>
  );
}


function FunnelDropoffPanel({ dropoff }) {
  if (!dropoff) return null;
  const hasWs = (dropoff.workspace || []).some((s) => s.count > 0);
  const hasPromos = (dropoff.promos || []).length > 0;
  if (!hasWs && !hasPromos) return null;
  const renderRow = (label, steps, testid) => {
    // Find the step index with the largest drop (skip negative / first).
    let worstIdx = -1, worstDrop = 5;
    steps.forEach((s, i) => {
      if (i > 0 && s.drop_from_prev_pct > worstDrop) {
        worstDrop = s.drop_from_prev_pct; worstIdx = i;
      }
    });
    return (
      <div className="mb-3 last:mb-0" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1.5 flex items-center gap-2">
          <span>{label}</span>
          {worstIdx >= 0 && (
            <span className="text-rose-700 normal-case tracking-normal font-normal">
              biggest drop: <b>{steps[worstIdx].drop_from_prev_pct}%</b> between {steps[worstIdx - 1].label} → {steps[worstIdx].label}
            </span>
          )}
        </div>
        <div className="flex items-stretch gap-0">
          {steps.map((s, i) => {
            const worst = i === worstIdx;
            // Heatmap intensity based on retention — 100% = full color, 0 = pale
            const intensity = Math.max(0, Math.min(100, s.retention_pct));
            const bg = worst
              ? `rgba(244, 63, 94, ${0.12 + (100 - intensity) / 400})`
              : `rgba(184, 134, 11, ${0.06 + intensity / 350})`;
            const border = worst ? "border-rose-400" : "border-amber-200";
            return (
              <div key={s.step} className={`flex-1 border ${border} p-2 min-w-0`}
                     style={{ background: bg }}>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground truncate">{s.label}</div>
                <div className="text-lg font-bold font-mono" style={{ color: worst ? "#B91C1C" : "#1F2937" }}>{s.count}</div>
                <div className="text-[10px] font-mono flex justify-between gap-1">
                  <span className="text-muted-foreground">{s.retention_pct}%</span>
                  {i > 0 && (
                    <span className={worst ? "text-rose-700 font-bold" : "text-muted-foreground"}>
                      {s.drop_from_prev_pct < 0 ? "—" : `↓ ${s.drop_from_prev_pct}%`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  return (
    <div className="border border-border p-4 mb-5 bg-gradient-to-br from-amber-50/30 to-white" data-testid="sb-funnel-dropoff">
      <div className="flex items-center gap-2 mb-3">
        <IndianRupee className="w-4 h-4 text-amber-700" />
        <h2 className="text-sm font-bold uppercase tracking-widest">Where customers drop off</h2>
        <span className="text-[10px] text-muted-foreground normal-case tracking-normal font-normal">
          Landed → Paid · workspace + per share-linked promo
        </span>
      </div>
      {hasWs && renderRow("Overall widget funnel", dropoff.workspace, "sb-funnel-ws")}
      {hasPromos && dropoff.promos.map((p) => (
        <div key={p.id}>
          {renderRow(
            <span className="font-mono">{p.code}<span className="ml-1 text-muted-foreground/70 normal-case tracking-normal font-normal">{p.name ? ` · ${p.name}` : ""}</span></span>,
            p.steps, `sb-funnel-promo-${p.id}`
          )}
        </div>
      ))}
    </div>
  );
}


// Feb 2026 · Share-link button on a promo row — generates a `/b/<slug>`
// short URL and copies it. On second click, re-copies the same URL.
function ShareLinkButton({ ws, promo }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/promos/${promo.id}/share-link`);
      const abs = data.url?.startsWith("http") ? data.url
        : `${window.location.origin}/b/${data.slug}`;
      setUrl(abs);
      try { await navigator.clipboard.writeText(abs); toast.success("Share link copied"); }
      catch { toast.info("Link ready — copy manually"); }
    } catch (e) { toast.error(apiErrorMessage(e, "Share link failed")); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy}
            className="text-[10px] uppercase tracking-widest font-semibold text-primary border border-primary/40 px-2 py-1 hover:bg-primary/5 inline-flex items-center gap-1"
            title={url || "Generate a shareable /b/… link that pre-fills this promo on the booking widget"}
            data-testid={`sb-promo-share-${promo.id}`}>
      <LinkIcon className="w-3 h-3" /> {busy ? "…" : url ? "Copy again" : "Share link"}
    </button>
  );
}


function PromoDialog({ ws, promo, spaces, packages, onClose, onDone }) {
  const isNew = !promo;
  const [form, setForm] = useState(() => ({
    code: promo?.code || "",
    name: promo?.name || "",
    description: promo?.description || "",
    type: promo?.type || "percent",
    value: promo?.value ?? 10,
    starts_at: promo?.starts_at ? promo.starts_at.slice(0, 16) : "",
    ends_at: promo?.ends_at ? promo.ends_at.slice(0, 16) : "",
    min_hours: promo?.min_hours ?? "",
    max_uses: promo?.max_uses ?? "",
    space_ids: promo?.space_ids || [],
    package_ids: promo?.package_ids || [],
    auto_apply: !!promo?.auto_apply,
    active: promo?.active !== false,
  }));
  const [busy, setBusy] = useState(false);

  const toggleId = (key, id) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
    }));
  };

  const submit = async () => {
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    if (form.value == null || Number(form.value) <= 0) { toast.error("Discount value must be > 0"); return; }
    if (form.type === "percent" && Number(form.value) > 100) { toast.error("Percent cannot exceed 100"); return; }
    setBusy(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim(),
        type: form.type,
        value: Number(form.value),
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        min_hours: form.min_hours === "" ? null : Number(form.min_hours),
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        space_ids: form.space_ids,
        package_ids: form.package_ids,
        auto_apply: form.auto_apply,
        active: form.active,
      };
      if (isNew) await api.post(`/workspaces/${ws}/studio/promos`, body);
      else await api.patch(`/workspaces/${ws}/studio/promos/${promo.id}`, body);
      toast.success(isNew ? "Promo created" : "Promo updated");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="sb-promo-dialog">
        <DialogHeader className="p-4 sm:p-5 border-b border-border sticky top-0 bg-background z-10">
          <DialogTitle className="text-base sm:text-lg">{isNew ? "New promo" : "Edit promo"}</DialogTitle>
        </DialogHeader>
        <div className="p-4 sm:p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-1">
              <Label className="text-xs">Code *</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                     placeholder="LAUNCH20" className="rounded-none h-9 font-mono" data-testid="sb-promo-code" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Internal name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Launch offer" className="rounded-none h-9" data-testid="sb-promo-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Discount type</Label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                       className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                       data-testid="sb-promo-type">
                <option value="percent">Percent (%)</option>
                <option value="flat">Flat (₹ off)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Value {form.type === "percent" ? "(%)" : "(₹)"}</Label>
              <Input type="number" min={0} step="0.5" value={form.value}
                     onChange={(e) => setForm({ ...form, value: e.target.value })}
                     className="rounded-none h-9" data-testid="sb-promo-value" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Valid from</Label>
              <Input type="datetime-local" value={form.starts_at}
                     onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                     className="rounded-none h-9" data-testid="sb-promo-starts" />
            </div>
            <div>
              <Label className="text-xs">Valid until</Label>
              <Input type="datetime-local" value={form.ends_at}
                     onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                     className="rounded-none h-9" data-testid="sb-promo-ends" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Min booking hours</Label>
              <Input type="number" min={0} step="0.5" value={form.min_hours}
                     onChange={(e) => setForm({ ...form, min_hours: e.target.value })}
                     placeholder="Any duration" className="rounded-none h-9" data-testid="sb-promo-minhours" />
            </div>
            <div>
              <Label className="text-xs">Max total uses</Label>
              <Input type="number" min={0} step={1} value={form.max_uses}
                     onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                     placeholder="Unlimited" className="rounded-none h-9" data-testid="sb-promo-maxuses" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Scope · Spaces <span className="text-muted-foreground font-normal ml-1">(none = all)</span></Label>
            <div className="border border-border max-h-32 overflow-y-auto mt-1" data-testid="sb-promo-spaces-list">
              {spaces.length === 0 ? (
                <div className="p-2 text-[11px] text-muted-foreground italic">No spaces defined.</div>
              ) : spaces.map((s) => (
                <label key={s.id} className="flex items-center gap-2 px-2 py-1 border-b border-border last:border-b-0 text-xs cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={form.space_ids.includes(s.id)}
                         onChange={() => toggleId("space_ids", s.id)}
                         data-testid={`sb-promo-space-${s.id}`} />
                  <span>{s.name}</span>
                </label>
              ))}
            </div>
          </div>
          {packages.length > 0 && (
            <div>
              <Label className="text-xs">Scope · Packages <span className="text-muted-foreground font-normal ml-1">(none = all)</span></Label>
              <div className="border border-border max-h-32 overflow-y-auto mt-1" data-testid="sb-promo-pkgs-list">
                {packages.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 px-2 py-1 border-b border-border last:border-b-0 text-xs cursor-pointer hover:bg-muted/30">
                    <input type="checkbox" checked={form.package_ids.includes(p.id)}
                           onChange={() => toggleId("package_ids", p.id)}
                           data-testid={`sb-promo-pkg-${p.id}`} />
                    <span>{p.name}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">₹{p.price}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs">Description (shown internally)</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       rows={2} className="rounded-none text-xs"
                       placeholder="First-time customer welcome offer…"
                       data-testid="sb-promo-desc" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-start gap-2 p-2 border border-border text-xs cursor-pointer"
                    data-testid="sb-promo-autoapply">
              <input type="checkbox" checked={form.auto_apply}
                     onChange={(e) => setForm({ ...form, auto_apply: e.target.checked })}
                     className="mt-0.5" />
              <span>
                <strong>Auto-apply</strong>
                <div className="text-[10px] text-muted-foreground">No code needed — applied silently on every eligible quote.</div>
              </span>
            </label>
            <label className="flex items-start gap-2 p-2 border border-border text-xs cursor-pointer"
                    data-testid="sb-promo-active">
              <input type="checkbox" checked={form.active}
                     onChange={(e) => setForm({ ...form, active: e.target.checked })}
                     className="mt-0.5" />
              <span>
                <strong>Active</strong>
                <div className="text-[10px] text-muted-foreground">Uncheck to pause without deleting.</div>
              </span>
            </label>
          </div>
        </div>
        <DialogFooter className="p-4 sm:p-5 border-t border-border sticky bottom-0 bg-background z-10">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="sb-promo-save">
            {busy ? "Saving…" : (isNew ? "Create promo" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 · Abandoned Carts — bookings stuck at pending_payment.
// Shows nudge status per row + a manual "send now" button + workspace
// opt-in toggle for the 15-min sweeper.
// Feb 2026 · Studio Additional Services — free-text catalogue that
// shows on the booking widget Step 3 as a multi-select. Ticked services
// spawn a Custom Shoot enquiry on booking submit for a separate quote.
function AdditionalServices({ ws, canManage }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "" });
  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio-services`);
      setRows(data.services || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws]);

  const persist = async (next) => {
    setBusy(true);
    try {
      // Backend expects a bare list of {name, description, active}
      const payload = next.map(({ name, description, active }) => ({
        name: (name || "").trim(),
        description: (description || "").trim(),
        active: active !== false,
      }));
      const { data } = await api.put(`/workspaces/${ws}/studio-services`, payload);
      setRows(data.services || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const add = async () => {
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    await persist([...rows, { id: crypto.randomUUID(), name: draft.name.trim(), description: draft.description.trim(), active: true }]);
    setDraft({ name: "", description: "" });
    toast.success("Service added");
  };
  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await persist(rows.filter((s) => s.id !== id));
    toast.success("Service removed");
  };
  const toggle = (id) => persist(rows.map((s) => s.id === id ? { ...s, active: !s.active } : s));
  const move = (idx, dir) => {
    const arr = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    persist(arr);
  };
  const patch = (id, field, val) => persist(rows.map((s) => s.id === id ? { ...s, [field]: val } : s));

  return (
    <div data-testid="sb-services">
      <div className="mb-4 border border-border p-4 bg-gradient-to-br from-amber-50/30 to-white">
        <h2 className="text-sm font-bold uppercase tracking-widest mb-1">Additional services</h2>
        <p className="text-[11px] text-muted-foreground max-w-lg">
          Shown on the public booking widget in Step 3 (Extras). Ticking any of these on a booking spawns a Custom Shoot enquiry — a separate quotation is sent by email.
        </p>
      </div>
      {canManage && (
        <div className="flex flex-wrap gap-2 mb-3 items-end" data-testid="sb-service-new">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] uppercase tracking-widest">Service name</Label>
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Reels editing" className="rounded-none h-9" data-testid="sb-service-name" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[10px] uppercase tracking-widest">Short description (optional)</Label>
            <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="Vertical edits with color grade" className="rounded-none h-9" data-testid="sb-service-desc" />
          </div>
          <Button size="sm" className="rounded-none h-9" onClick={add} disabled={busy} data-testid="sb-service-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
      )}
      <div className="border border-border">
        {rows.length === 0 && (
          <div className="p-4 text-center text-muted-foreground italic text-xs">No services yet — add your first above.</div>
        )}
        {rows.map((s, idx) => (
          <div key={s.id} className={`flex items-center gap-2 p-2 border-b border-border last:border-b-0 ${!s.active ? "opacity-50" : ""}`}
               data-testid={`sb-service-row-${s.id}`}>
            {canManage && (
              <div className="flex flex-col text-[10px] text-muted-foreground">
                <button onClick={() => move(idx, -1)} disabled={idx === 0}
                        className="hover:text-primary disabled:opacity-30" data-testid={`sb-service-up-${s.id}`}>▲</button>
                <button onClick={() => move(idx, 1)} disabled={idx === rows.length - 1}
                        className="hover:text-primary disabled:opacity-30" data-testid={`sb-service-down-${s.id}`}>▼</button>
              </div>
            )}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
              <Input value={s.name} onChange={(e) => patch(s.id, "name", e.target.value)}
                     className="rounded-none h-8 text-xs" disabled={!canManage} />
              <Input value={s.description || ""} onChange={(e) => patch(s.id, "description", e.target.value)}
                     className="rounded-none h-8 text-xs" placeholder="Description" disabled={!canManage} />
            </div>
            {canManage && (
              <>
                <button onClick={() => toggle(s.id)} className="text-[10px] uppercase tracking-widest border border-border px-2 py-1 hover:bg-muted"
                        data-testid={`sb-service-toggle-${s.id}`}>
                  {s.active ? "Active" : "Off"}
                </button>
                <button onClick={() => del(s.id)} className="text-rose-600 hover:text-rose-800"
                        data-testid={`sb-service-delete-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function AbandonedCarts({ ws, canManage }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({ enabled: false, channels: ["email", "whatsapp"] });
  const [kpis, setKpis] = useState(null);
  const load = async () => {
    try {
      const [r, s, k] = await Promise.all([
        api.get(`/workspaces/${ws}/studio/abandoned-carts`),
        api.get(`/workspaces/${ws}/studio/abandoned-carts/settings`),
        api.get(`/workspaces/${ws}/studio/abandoned-carts/kpis`),
      ]);
      setRows(r.data.carts || []);
      setCfg(s.data.settings || { enabled: false, channels: ["email", "whatsapp"] });
      setKpis(k.data.kpis || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws]);

  const saveCfg = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/workspaces/${ws}/studio/abandoned-carts/settings`, next);
      setCfg(data.settings);
      toast.success(next.enabled ? "Nudges enabled — sweeper runs every 15 min" : "Nudges paused");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const sweep = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/abandoned-carts/sweep`);
      const sent = data.sent || {};
      if (sent.skipped === "opt_in_required") {
        toast.info("Nudges are paused — enable them first to send reminders");
      } else {
        toast.success(`Sweep done · +30m ${sent.nudge_1 || 0} · +6h ${sent.nudge_2 || 0}`);
      }
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Sweep failed")); }
    finally { setBusy(false); }
  };

  const nudgeStatus = (c) => {
    const n = c.abandoned_nudges_sent || {};
    if (n.nudge_2) return { label: "Both nudges sent", cls: "bg-slate-100 text-slate-700 border-slate-300" };
    if (n.nudge_1) return { label: "30-min sent", cls: "bg-amber-50 text-amber-800 border-amber-300" };
    return { label: "Not yet", cls: "bg-white text-muted-foreground border-border" };
  };

  return (
    <div data-testid="sb-abandoned-carts">
      {/* Recovery KPI strip — nudges → paid conversion, shown before
          the settings panel so admins get the punchline first. */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="sb-ac-kpis">
          {[
            { k: "recovery_rate", label: "Recovery rate", fmt: (v) => `${v}%`,
              hint: "% of nudged bookings that ended up paid", tone: (v) => v >= 20 ? "emerald" : v >= 10 ? "amber" : "rose" },
            { k: "revenue_recovered", label: "Revenue rescued", fmt: (v) => `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
              hint: "Total ₹ from bookings that paid after a nudge", tone: () => "emerald" },
            { k: "recovered", label: "Recovered", fmt: (v) => v,
              hint: "Bookings paid after receiving at least 1 nudge", tone: () => "primary" },
            { k: "stuck_now", label: "Still winnable", fmt: (v) => v,
              hint: "Nudged but not yet paid — still recoverable", tone: (v) => v > 0 ? "amber" : "neutral" },
          ].map((m) => {
            const v7 = kpis.d7?.[m.k] ?? 0;
            const v30 = kpis.d30?.[m.k] ?? 0;
            const tone = m.tone(v7);
            const toneCls = tone === "emerald" ? "border-emerald-300 bg-emerald-50/40"
              : tone === "amber" ? "border-amber-300 bg-amber-50/40"
              : tone === "rose"  ? "border-rose-300 bg-rose-50/40"
              : tone === "primary" ? "border-primary/30 bg-primary/5"
              : "border-border bg-white";
            const numCls = tone === "emerald" ? "text-emerald-700"
              : tone === "amber" ? "text-amber-700"
              : tone === "rose"  ? "text-rose-700"
              : tone === "primary" ? "text-primary"
              : "text-foreground";
            return (
              <div key={m.k} data-testid={`sb-ac-kpi-${m.k}`}
                   className={`p-4 border ${toneCls}`}
                   title={m.hint}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{m.label}</div>
                <div className={`text-3xl font-bold mt-1 ${numCls}`} style={{ fontFamily: `"Cormorant Garamond", Georgia, serif`, fontWeight: 500, letterSpacing: "-0.02em" }}>
                  {m.fmt(v7)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                  <span className="uppercase tracking-widest font-semibold">7d</span>
                  <span>·</span>
                  <span>30d {m.fmt(v30)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Settings strip */}
      <div className="border border-border p-5 mb-5 bg-gradient-to-br from-amber-50/30 to-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-1.5">
              <Bell className="w-4 h-4 text-amber-700" />
              <h2 className="text-sm font-bold uppercase tracking-widest">Automatic nudges</h2>
              <span data-testid="sb-ac-status-badge"
                     className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border font-bold ${cfg.enabled ? "bg-emerald-50 text-emerald-800 border-emerald-400" : "bg-neutral-100 text-neutral-500 border-neutral-300"}`}>
                {cfg.enabled ? "● Live" : "○ Paused"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground max-w-lg">
              A background job sweeps every 15 minutes and nudges customers who reached checkout but didn't pay — first at <b>+30 min</b>, then <b>+6 hr</b>. Hard-stop after 2 nudges.
              Uses your Cashfree order id so the payment link re-activates the same order.
            </p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-none" onClick={sweep} disabled={busy}
                      data-testid="sb-ac-sweep">
                <Send className="w-3.5 h-3.5 mr-1" /> Run sweep now
              </Button>
              <Button variant={cfg.enabled ? "secondary" : "default"} size="sm" className="rounded-none"
                      onClick={() => saveCfg({ ...cfg, enabled: !cfg.enabled })}
                      disabled={busy}
                      data-testid="sb-ac-toggle">
                {cfg.enabled ? "Pause nudges" : "Enable nudges"}
              </Button>
            </div>
          )}
        </div>
        {canManage && cfg.enabled && (
          <div className="mt-3 flex flex-wrap items-center gap-3 pt-3 border-t border-amber-100">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Channels</div>
            {["email", "whatsapp"].map((ch) => {
              const on = cfg.channels?.includes(ch);
              return (
                <label key={ch} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={on}
                         onChange={() => {
                           const nextCh = on ? cfg.channels.filter((x) => x !== ch)
                                              : [...(cfg.channels || []), ch];
                           saveCfg({ ...cfg, channels: nextCh });
                         }}
                         data-testid={`sb-ac-channel-${ch}`} />
                  <span className="capitalize">{ch}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Cart list */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              {["Booking", "Customer", "Slot", "Total", "Promo", "Started", "Status", ""].map((h, i) =>
                <th key={i} className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px] text-muted-foreground whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const st = nudgeStatus(c);
              const total = c.quote?.total || 0;
              const started = c.created_at?.slice(0, 16).replace("T", " ") || "—";
              const slot = c.slot_start?.slice(0, 16).replace("T", " ") || "—";
              return (
                <tr key={c.id} className="border-t border-border" data-testid={`sb-ac-row-${c.id}`}>
                  <td className="px-2 py-1.5 font-mono">{c.code}</td>
                  <td className="px-2 py-1.5">
                    <div className="truncate max-w-[160px]">{c.customer_name || "—"}</div>
                    {c.customer_email && <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">{c.customer_email}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-[11px] whitespace-nowrap">{slot}</td>
                  <td className="px-2 py-1.5 font-mono">₹{Number(total).toFixed(0)}</td>
                  <td className="px-2 py-1.5 text-[11px]">{c.promo_code ? <span className="font-mono bg-amber-50 border border-amber-200 px-1">{c.promo_code}</span> : "—"}</td>
                  <td className="px-2 py-1.5 text-[11px] whitespace-nowrap">{started}</td>
                  <td className="px-2 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 border ${st.cls}`}>{st.label}</span></td>
                  <td className="px-2 py-1.5 text-right"></td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-muted-foreground italic">
                No stuck bookings in the last 7 days — nice conversion!
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// R268yyy · Feb 2026 · Bookings tab rebuilt on top of AdvancedTable so
// admins get the same power tools they use on the Tasks list — column
// show/hide + reorder, per-column sort/filter, saved Views, group-by,

// Bookings table + detail dialog + child dialogs extracted to ./studioBooking/Bookings.jsx (Oct 2026).


function Shoots({ ws }) {
  const [rows, setRows] = useState([]);
  const [phaseFilter, setPhaseFilter] = useState("");
  const [search, setSearch] = useState("");
  const load = async () => {
    const { data } = await api.get(`/workspaces/${ws}/studio/shoots`);
    setRows(data.shoots || []);
  };
  useEffect(() => { if (ws) load(); }, [ws]);
  const advance = async (id, phase) => {
    try {
      await api.post(`/workspaces/${ws}/studio/shoots/${id}/phase`, { phase });
      toast.success(`Phase → ${phase}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const mintSignoff = async (id) => {
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/shoots/${id}/signoff-link`, {});
      const url = `${window.location.origin}${data.url}`;
      await navigator.clipboard.writeText(url);
      toast.success("Sign-off link copied to clipboard");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to mint sign-off link")); }
  };
  const openBooking = (bookingId) => {
    if (!bookingId) return;
    // Deep-link via query param that the Bookings tab reads to focus
    // the row — if the parent tab switcher isn't listening we still
    // open the booking page scoped to that id.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", "bookings");
      url.searchParams.set("focus_booking", bookingId);
      window.history.replaceState(null, "", url.toString());
      window.dispatchEvent(new CustomEvent("sb:focus-booking", { detail: { bookingId } }));
    } catch (e) { void e; }
  };
  const PHASES = ["planned", "confirmed", "equipment_out", "shoot_in_progress", "shoot_complete", "in_edit", "delivered", "invoiced"];
  const PHASE_LABEL = {
    planned: "Planned", confirmed: "Confirmed", equipment_out: "Equipment out",
    shoot_in_progress: "Shoot in progress", shoot_complete: "Shoot complete",
    in_edit: "In edit", delivered: "Delivered", invoiced: "Invoiced",
  };
  const PHASE_TONE = {
    planned: "bg-slate-100 text-slate-700 border-slate-300",
    confirmed: "bg-blue-50 text-blue-800 border-blue-300",
    equipment_out: "bg-amber-50 text-amber-900 border-amber-300",
    shoot_in_progress: "bg-emerald-50 text-emerald-800 border-emerald-400",
    shoot_complete: "bg-teal-50 text-teal-800 border-teal-300",
    in_edit: "bg-indigo-50 text-indigo-800 border-indigo-300",
    delivered: "bg-violet-50 text-violet-800 border-violet-300",
    invoiced: "bg-stone-100 text-stone-700 border-stone-300",
  };
  const fmtWhen = (s, e) => {
    if (!s) return "";
    try {
      const d = new Date(s);
      const d2 = e ? new Date(e) : null;
      const date = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
      const t1 = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
      const t2 = d2 ? d2.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";
      return `${date} · ${t1}${t2 ? ` → ${t2}` : ""}`;
    } catch (err) { return `${s?.slice(0, 16)} → ${e?.slice(0, 16) || ""}`; }
  };
  const q = (search || "").toLowerCase();
  const filtered = rows.filter((s) => {
    if (phaseFilter && s.phase !== phaseFilter) return false;
    if (!q) return true;
    const haystack = [
      s.booking?.customer_name, s.booking?.customer_email,
      s.booking?.code, s.booking?.ref_no, s.space?.name, s.studio?.name,
      s.client?.name, s.client?.company, s.project?.name,
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(q);
  });
  return (
    <div data-testid="sb-shoots-list" className="space-y-3">
      {/* Oct 2026 · Scannable Shoots tab. Each row ties back to the
          customer / project / booking with a one-click "Open booking"
          link, plus a search box + phase filter so a PM can spot the
          shoot they want in a long list. */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search by customer, booking # or space…"
                 className="pl-8 h-8 rounded-none text-xs"
                 data-testid="sb-shoots-search" />
        </div>
        <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}
                className="h-8 border border-border bg-background text-xs rounded-none px-2"
                data-testid="sb-shoots-filter-phase">
          <option value="">All phases ({rows.length})</option>
          {PHASES.map((p) => <option key={p} value={p}>{PHASE_LABEL[p]}</option>)}
        </select>
        <span className="text-[11px] text-muted-foreground ml-auto">{filtered.length} shown</span>
      </div>
      {filtered.map((s) => {
        const b = s.booking || {};
        const refLabel = b.ref_no ? `#${b.ref_no}` : (b.code || s.booking_code || "—");
        return (
        <div key={s.id} className="border border-border bg-background hover:shadow-sm transition-shadow p-3"
             data-testid={`sb-shoot-row-${s.id}`}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <button
                  onClick={() => openBooking(b.id)}
                  className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1"
                  title="Open booking in Bookings tab"
                  data-testid={`sb-shoot-open-booking-${s.id}`}
                >
                  {refLabel} <ArrowRight className="w-3 h-3" />
                </button>
                {b.customer_name && (
                  <span className="text-sm">· {b.customer_name}</span>
                )}
                {s.is_offsite && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800">OFFSITE</span>}
                {b.status && (
                  <span className="text-[10px] px-1.5 py-0.5 border border-border bg-muted text-muted-foreground">
                    booking: {b.status}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                <span>{fmtWhen(s.scheduled_start, s.scheduled_end)}</span>
                {s.space?.name && <span>· {s.space.name}</span>}
                {s.studio?.name && s.studio.name !== s.space?.name && <span>({s.studio.name})</span>}
                {typeof b.total === "number" && (
                  <span>· ₹{b.total.toLocaleString()}</span>
                )}
              </div>
              {(s.client?.name || s.project?.name) && (
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {s.client?.name && <span>Client: <b>{s.client.name}</b>{s.client.company ? ` (${s.client.company})` : ""}</span>}
                  {s.project?.name && (
                    <span className="ml-2">· Project: <b>{s.project.name}</b></span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {s.signoff_status && (
                <span className={`text-[10px] px-1.5 py-0.5 border ${s.signoff_status === "approved" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-amber-500 bg-amber-50 text-amber-800"}`}>
                  signoff: {s.signoff_status}
                </span>
              )}
              <div className={`text-[10px] px-2 py-0.5 border ${PHASE_TONE[s.phase] || "bg-primary/10 text-primary border-primary"} font-semibold`}>
                {PHASE_LABEL[s.phase] || s.phase}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 pt-2 border-t border-dashed border-border">
            <span className="text-[10px] text-muted-foreground mr-1 self-center">Advance to:</span>
            {PHASES.map((p) => (
              <button key={p} onClick={() => advance(s.id, p)}
                      disabled={s.phase === p}
                      className={`text-[10px] px-2 py-0.5 border rounded-none ${s.phase === p ? "bg-primary text-white border-primary" : "hover:bg-muted"}`}
                      data-testid={`sb-shoot-phase-${p}`}>{PHASE_LABEL[p]}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed border-border">
            <button onClick={() => mintSignoff(s.id)} data-testid={`sb-shoot-signoff-${s.id}`}
                    className="text-[10px] px-2 py-0.5 border border-primary text-primary hover:bg-primary hover:text-white rounded-none inline-flex items-center gap-1">
              <LinkIcon className="w-3 h-3" /> {s.signoff_token ? "Copy" : "Mint"} client sign-off link
            </button>
            {s.signoff_token && (
              <a href={`/deliver/${s.signoff_token}`} target="_blank" rel="noopener noreferrer"
                 className="text-[10px] text-primary hover:underline">
                Open preview →
              </a>
            )}
            {b.id && (
              <button onClick={() => openBooking(b.id)}
                      className="text-[10px] px-2 py-0.5 border border-border hover:bg-muted rounded-none inline-flex items-center gap-1 ml-auto"
                      data-testid={`sb-shoot-open-booking-btn-${s.id}`}>
                Open booking →
              </button>
            )}
          </div>
          <DeliverablesEditor ws={ws} shoot={s} onChanged={load} />
          {s.actual_start && (
            <div className="text-[10px] text-emerald-800 mt-2 font-mono">actual: {s.actual_start?.slice(11, 16)}{s.actual_end ? ` → ${s.actual_end.slice(11, 16)}` : " (in progress)"}</div>
          )}
        </div>
        );
      })}
      {filtered.length === 0 && (
        <div className="text-center py-8 text-muted-foreground italic">
          {rows.length === 0 ? "No shoots yet." : "No shoots match your search / filter."}
        </div>
      )}
    </div>
  );
}

function DeliverablesEditor({ ws, shoot, onChanged }) {
  const [name, setName] = useState("");
  const [url, setUrl]   = useState("");
  const [busy, setBusy] = useState(false);
  const items = shoot.deliverables || [];
  const add = async () => {
    if (!url.trim()) { toast.error("Paste a file URL"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${ws}/studio/shoots/${shoot.id}/deliverables`,
        { name: name.trim() || url.split("/").pop(), url: url.trim(), status: "delivered" });
      setName(""); setUrl("");
      onChanged && await onChanged();
      toast.success("Deliverable added");
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this deliverable?")) return;
    try {
      await api.delete(`/workspaces/${ws}/studio/shoots/${shoot.id}/deliverables/${id}`);
      onChanged && await onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="mt-2 pt-2 border-t border-dashed border-border" data-testid={`sb-shoot-deliverables-${shoot.id}`}>
      <div className="flex items-center gap-1 mb-1">
        <ImageIcon className="w-3 h-3 text-muted-foreground" />
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Deliverables ({items.length})</span>
      </div>
      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2 mb-2">
          {items.map((d) => (
            <div key={d.id} className="border border-border relative group">
              <MiniPreview d={d} />
              <button onClick={() => del(d.id)} data-testid={`sb-deliverable-del-${d.id}`}
                      className="absolute top-0.5 right-0.5 bg-white/90 hover:bg-rose-500 hover:text-white text-rose-600 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 className="w-3 h-3" />
              </button>
              <div className="p-1 text-[10px] truncate">{d.name}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-1">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Label (optional)"
                className="rounded-none h-7 text-xs flex-1" data-testid={`sb-deliverable-name-${shoot.id}`} />
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
                className="rounded-none h-7 text-xs flex-1" data-testid={`sb-deliverable-url-${shoot.id}`} />
        <Button size="sm" className="rounded-none h-7 text-xs" onClick={add} disabled={busy}
                data-testid={`sb-deliverable-add-${shoot.id}`}>
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function MiniPreview({ d }) {
  const url = d.url || "";
  const mime = (d.mime || "").toLowerCase();
  const isImage = mime.startsWith("image/") || /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url);
  const isVideo = mime.startsWith("video/") || /\.(mp4|mov|webm)(\?|$)/i.test(url);
  if (isImage) return <img src={d.thumb_url || url} alt="" className="w-full h-16 object-cover" loading="lazy" />;
  if (isVideo) return <div className="w-full h-16 bg-neutral-800 flex items-center justify-center text-white text-[10px]">▶ VIDEO</div>;
  return <div className="w-full h-16 bg-neutral-100 flex items-center justify-center text-neutral-400 text-[10px]">FILE</div>;
}

// Widget config editor extracted to ./studioBooking/WidgetConfig.jsx (Oct 2026).

function Heatmap({ ws }) {
  const today = new Date();
  const inThirty = new Date(); inThirty.setDate(today.getDate() + 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [from, setFrom] = useState(fmt(today));
  const [to, setTo]     = useState(fmt(inThirty));
  const [data, setData] = useState(null);
  // Feb 2026 · R268yyy · Click-to-book / click-to-edit. Manual dialog
  // gets prefilled with space + date; if the clicked cell already has
  // bookings we load its first booking into the Edit dialog instead.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualPrefill, setManualPrefill] = useState(null);
  const [detailBooking, setDetailBooking] = useState(null);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio/heatmap`, { params: { from_date: from, to_date: to } });
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Heatmap load failed")); }
  };
  const loadRefs = async () => {
    try {
      const [mRes, cRes, pRes, sRes] = await Promise.all([
        api.get(`/workspaces/${ws}/members`),
        api.get(`/workspaces/${ws}/clients`),
        api.get(`/workspaces/${ws}/projects/picker`),
        api.get(`/workspaces/${ws}/studio/spaces`),
      ]);
      setMembers(Array.isArray(mRes.data) ? mRes.data : []);
      setClients(Array.isArray(cRes.data) ? cRes.data : []);
      setProjects(Array.isArray(pRes.data) ? pRes.data : []);
      setSpaces(sRes.data?.spaces || []);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { if (ws) { load(); loadRefs(); } /* eslint-disable-next-line */ }, [ws]);
  const cellColor = (hours, bh) => {
    if (!hours) return "bg-neutral-100";
    const pct = Math.min(100, (hours / bh) * 100);
    if (pct < 25)  return "bg-emerald-200";
    if (pct < 50)  return "bg-amber-200";
    if (pct < 75)  return "bg-orange-300";
    return "bg-rose-400";
  };
  const openCell = async (space, date, cell) => {
    if (cell?.count && cell?.booking_ids?.length) {
      // Load that booking (fetch full row from bookings list)
      try {
        const { data: bd } = await api.get(`/workspaces/${ws}/studio/bookings`);
        const row = (bd.bookings || []).find((b) => b.id === cell.booking_ids[0]);
        if (row) { setDetailBooking(row); return; }
      } catch (_e) { /* fall through to manual */ }
    }
  };
  // Feb 2026 · R280 · Empty-cell click opens an inline popover with
  // start + end time dropdowns instead of blindly defaulting to
  // 10 AM–2 PM. Snaps to 30-min ticks. Submits by opening the Manual
  // Booking dialog with the chosen window pre-filled.
  const openWithTimes = (space, date, startHHMM, endHHMM) => {
    setManualPrefill({
      space_id: space.id,
      slot_start: `${date}T${startHHMM}:00`,
      slot_end:   `${date}T${endHHMM}:00`,
    });
    setManualOpen(true);
  };
  return (
    <div data-testid="sb-heatmap" className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-end flex-wrap">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none h-8 w-40" data-testid="sb-heatmap-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none h-8 w-40" data-testid="sb-heatmap-to" />
        </div>
        <Button size="sm" className="rounded-none h-8" onClick={load} data-testid="sb-heatmap-refresh">Refresh</Button>
        <div className="flex-1" />
        <Button size="sm" className="rounded-none h-8"
                 onClick={() => { setManualPrefill(null); setManualOpen(true); }}
                 data-testid="sb-heatmap-manual-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add manual booking
        </Button>
      </div>
      <div className="text-[10px] text-muted-foreground italic">
        Tip: click any date cell — free cells open a time-picker so you can pick the exact slot before creating a manual booking; booked cells jump straight to Edit Booking.
      </div>
      {data && (
        <div className="overflow-x-auto border border-border">
          <table className="text-[10px]">
            <thead className="bg-muted/50">
              <tr>
                <th className="sticky left-0 bg-muted/50 text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-muted-foreground">Space</th>
                {data.dates.map((d) => (
                  <th key={d} className="px-1 py-1.5 font-mono text-muted-foreground min-w-[28px]">{d.slice(5)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.spaces.map((sp) => (
                <tr key={sp.id} className="border-t border-border">
                  <td className="sticky left-0 bg-white px-2 py-1 font-semibold whitespace-nowrap">{sp.name}</td>
                  {data.dates.map((d) => {
                    const cell = data.cells?.[sp.id]?.[d];
                    const hours = cell?.hours || 0;
                    const busy = !!(cell?.count && cell?.booking_ids?.length);
                    const title = cell
                      ? `${hours}h · ${cell.count} bookings\n${(cell.customers || []).join(", ")}\nClick to open`
                      : "Free · click to pick time & book";
                    if (busy) {
                      return (
                        <td key={d}
                            onClick={() => openCell(sp, d, cell)}
                            role="button" tabIndex={0}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openCell(sp, d, cell)}
                            className={`px-1 py-1 text-center font-mono cursor-pointer hover:ring-2 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary ${cellColor(hours, data.business_hours)}`}
                            title={title}
                            data-testid={`sb-heatmap-cell-${sp.id}-${d}`}>
                          {hours ? hours.toFixed(1) : ""}
                        </td>
                      );
                    }
                    return (
                      <HeatmapEmptyCell key={d} space={sp} date={d} onPick={openWithTimes}
                                            title={title}
                                            cellColor={cellColor(hours, data.business_hours)} />
                    );
                  })}
                </tr>
              ))}
              {data.spaces.length === 0 && <tr><td colSpan={data.dates.length + 1} className="text-center py-6 text-muted-foreground italic">No spaces configured yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        <span>Legend:</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-emerald-200 inline-block" /> Light</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-amber-200 inline-block" /> Medium</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-orange-300 inline-block" /> Busy</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-rose-400 inline-block" /> Peak</span>
        <span className="ml-2">({data?.business_hours || 12}h business day)</span>
      </div>
      <ManualBookingDialog ws={ws} open={manualOpen}
                             prefill={manualPrefill}
                             onClose={() => { setManualOpen(false); setManualPrefill(null); }}
                             onDone={() => { setManualOpen(false); setManualPrefill(null); load(); }} />
      <BookingDetailDialog ws={ws} booking={detailBooking}
                              spaces={spaces} members={members}
                              clients={clients} projects={projects}
                              canManage
                              onClose={() => setDetailBooking(null)}
                              onChanged={load} />
    </div>
  );
}

// statusColor extracted to ./studioBooking/common.jsx (Oct 2026).

// StudioMailProfilePicker extracted to ./studioBooking/WidgetConfig.jsx (Oct 2026).

// Feb 2026 · Availability editor — days of week + open/close hours + blackouts.
function AvailabilityEditor({ value, onChange, testid }) {
  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const days = value?.availability_days ?? [0, 1, 2, 3, 4, 5];
  const openH = value?.open_hour ?? 9;
  const closeH = value?.close_hour ?? 21;
  const blackouts = value?.blackout_dates || [];
  const [newBlackout, setNewBlackout] = useState("");
  const toggleDay = (d) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort();
    onChange({ availability_days: next });
  };
  const addBlackout = () => {
    if (!newBlackout || blackouts.includes(newBlackout)) return;
    onChange({ blackout_dates: [...blackouts, newBlackout].sort() });
    setNewBlackout("");
  };
  return (
    <div className="border border-border p-3 space-y-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Availability calendar</div>
      <div>
        <Label className="text-[11px]">Open days</Label>
        <div className="flex gap-1 mt-1">
          {DAY_LABELS.map((lbl, i) => (
            <button type="button" key={i} onClick={() => toggleDay(i)}
                     data-testid={`${testid}-day-${i}`}
                     className={`px-2 py-1 text-[10px] border rounded-none ${days.includes(i) ? "bg-primary text-white border-primary" : "border-border hover:bg-muted"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Open at (hour · 0-23)</Label>
          <Input type="number" min={0} max={23} value={openH}
                  onChange={(e) => onChange({ open_hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
                  className="rounded-none h-8" data-testid={`${testid}-open`} />
        </div>
        <div>
          <Label className="text-[11px]">Close at (hour · 0-24)</Label>
          <Input type="number" min={0} max={24} value={closeH}
                  onChange={(e) => onChange({ close_hour: Math.max(0, Math.min(24, Number(e.target.value) || 0)) })}
                  className="rounded-none h-8" data-testid={`${testid}-close`} />
        </div>
      </div>
      <div>
        <Label className="text-[11px]">Blackout dates (Holidays)</Label>
        <div className="flex gap-1 mt-1">
          <Input type="date" value={newBlackout} onChange={(e) => setNewBlackout(e.target.value)}
                  className="rounded-none h-8 flex-1" data-testid={`${testid}-blackout-input`} />
          <Button size="sm" type="button" className="rounded-none h-8" onClick={addBlackout}
                   data-testid={`${testid}-blackout-add`}>Add</Button>
        </div>
        {blackouts.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {blackouts.map((d) => (
              <span key={d} className="text-[10px] px-1.5 py-0.5 border border-border font-mono">
                {d}
                <button type="button" onClick={() => onChange({ blackout_dates: blackouts.filter((x) => x !== d) })}
                        className="ml-1 text-rose-600 hover:text-rose-800">×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// Feb 2026 · Manual booking — internal shoot, no Cashfree charge.
// Supports admin off-hours override; slot time in 30-min increments;
// searchable client + project pickers.

/** 30-min-increment time picker used across Studio Booking admin dialogs. */
// ManualBookingDialog + TimeSelect30 extracted to ./studioBooking/ManualBooking.jsx (Oct 2026).


// Feb 2026 · Crew read-only bookings list — no rates, no deposits.
function CrewBookings({ ws }) {
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio/crew-bookings`, { params: { from_date: from } });
      setRows(data.bookings || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws]);
  return (
    <div data-testid="sb-crew-bookings">
      <div className="flex items-end gap-2 mb-3">
        <div>
          <Label className="text-xs">From date</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                  className="rounded-none h-8 w-40" data-testid="sb-crew-from" />
        </div>
        <Button size="sm" className="rounded-none h-8" onClick={load}>Refresh</Button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              {["Code", "When", "Duration", "Space", "Customer", "Status"].map((h) =>
                <th key={h} className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px] text-muted-foreground whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`sb-crew-booking-${r.id}`}>
                <td className="px-2 py-1.5 font-mono">{r.code}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">{r.slot_start?.replace("T", " ").slice(0, 16)}</td>
                <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">
                  {r.slot_end?.slice(11, 16)}
                </td>
                <td className="px-2 py-1.5">{r.is_offsite ? "🚚 Offsite" : (r.space_id?.slice(0, 8) || "—")}</td>
                <td className="px-2 py-1.5">{r.customer_name}
                  {r.is_internal && <span className="text-[9px] uppercase tracking-widest ml-1 bg-amber-100 text-amber-800 px-1">Internal</span>}
                </td>
                <td className="px-2 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 border ${statusColor(r.status)}`}>{r.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground italic">No upcoming bookings.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Feb 2026 · Unified studio calendar — month grid OR hour-by-hour day rail.
// Day view stacks each space as a column and lays out bookings by time
// with 30-min precision. Admins can drag a crew chip from the sidebar
// onto any booking to add them to the crew list (without opening a
// dialog) — HTML5 native drag/drop, no library.
function StudioCalendar({ ws, canManage }) {
  const [view, setView] = useState("month");   // "month" | "day"
  const [rows, setRows] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [anchor, setAnchor] = useState(() => {
    const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() };
  });
  // Day anchor = ISO date string. Kept separate from month anchor so
  // toggling views doesn't lose the user's month scroll position.
  const [dayAnchor, setDayAnchor] = useState(() => new Date().toISOString().slice(0, 10));
  const [focus, setFocus] = useState(null);
  const [wsIcsUrl, setWsIcsUrl] = useState("");
  const [wsIcsBusy, setWsIcsBusy] = useState(false);

  const load = async () => {
    try {
      let start, end;
      if (view === "day") {
        start = new Date(dayAnchor + "T00:00:00");
        end = new Date(dayAnchor + "T23:59:59");
      } else {
        start = new Date(anchor.y, anchor.m, 1);
        end = new Date(anchor.y, anchor.m + 1, 0, 23, 59, 59);
      }
      const { data } = await api.get(`/workspaces/${ws}/studio/availability`, {
        params: { from_date: start.toISOString().slice(0, 10),
                    to_date: end.toISOString().slice(0, 10) },
      });
      setRows(data.bookings || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ },
             [ws, anchor.y, anchor.m, dayAnchor, view]);
  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/members`).then((r) => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get(`/workspaces/${ws}/clients`).then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get(`/workspaces/${ws}/studio/spaces`).then((r) => setSpaces(r.data.spaces || [])).catch(() => {});
  }, [ws]);

  const first = new Date(anchor.y, anchor.m, 1);
  const monthLabel = first.toLocaleString("en", { month: "long", year: "numeric" });
  const startWeekday = first.getDay();
  const daysInMonth = new Date(anchor.y, anchor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const byDay = {};
  for (const b of rows) {
    const k = (b.slot_start || "").slice(0, 10);
    if (!k) continue;
    (byDay[k] = byDay[k] || []).push(b);
  }

  const move = (n) => {
    const d = new Date(anchor.y, anchor.m + n, 1);
    setAnchor({ y: d.getFullYear(), m: d.getMonth() });
  };
  const jumpToday = () => {
    const d = new Date();
    setAnchor({ y: d.getFullYear(), m: d.getMonth() });
  };
  const mintWsIcs = async () => {
    setWsIcsBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/workspace-ics-token`);
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setWsIcsUrl(abs);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not generate feed")); }
    finally { setWsIcsBusy(false); }
  };
  const copyWsIcs = async () => {
    try {
      await navigator.clipboard.writeText(wsIcsUrl);
      toast.success("Workspace iCal URL copied");
    } catch { toast.error("Copy failed"); }
  };
  const isoDay = (d) => `${anchor.y}-${String(anchor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayIso = new Date().toISOString().slice(0, 10);
  const statusDot = (s) => (
    s === "confirmed" ? "bg-emerald-500"
    : s === "in_progress" ? "bg-sky-500"
    : s === "completed" ? "bg-slate-400"
    : s === "cancelled" ? "bg-rose-400"
    : "bg-amber-500"
  );
  const clientName = (id) => clients.find((c) => c.id === id)?.name;
  const memberName = (id) => {
    const m = members.find((x) => (x.user_id || x.id) === id);
    return m?.name || m?.email;
  };

  // Feb 2026 · Day view helpers — 30-min grid from 06:00 → 23:00
  const DAY_START_HR = 6;
  const DAY_END_HR = 23;
  const SLOT_MINUTES = 30;
  const ROW_PX = 26; // per 30-min slot
  const totalSlots = ((DAY_END_HR - DAY_START_HR) * 60) / SLOT_MINUTES;
  const halfHours = [];
  for (let m = DAY_START_HR * 60; m < DAY_END_HR * 60; m += SLOT_MINUTES) {
    halfHours.push({ h: Math.floor(m / 60), m: m % 60,
                       label: `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}` });
  }
  const slotIndex = (isoTs) => {
    if (!isoTs) return 0;
    const d = new Date(isoTs);
    const mins = d.getHours() * 60 + d.getMinutes();
    return Math.max(0, Math.min(totalSlots, (mins - DAY_START_HR * 60) / SLOT_MINUTES));
  };
  const bookingsForDayView = rows.filter((b) => (b.slot_start || "").slice(0, 10) === dayAnchor);
  const moveDay = (n) => {
    const d = new Date(dayAnchor + "T00:00:00");
    d.setDate(d.getDate() + n);
    setDayAnchor(d.toISOString().slice(0, 10));
  };
  const jumpTodayDay = () => setDayAnchor(new Date().toISOString().slice(0, 10));
  const dayLabel = new Date(dayAnchor + "T00:00:00").toLocaleDateString("en", {
    weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // Feb 2026 · DnD crew assign. Drag a chip from the sidebar onto a
  // booking block; drop appends the user to assigned_user_ids via
  // `PUT /bookings/{id}/crew` with mode=add (does NOT clobber existing
  // crew, and does NOT create a task — see note on the dialog for the
  // task-syncing manual flow).
  const [dragUser, setDragUser] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const onCrewDrop = async (bookingId, userId) => {
    if (!bookingId || !userId) return;
    try {
      const { data } = await api.put(
        `/workspaces/${ws}/studio/bookings/${bookingId}/crew`,
        { user_ids: [userId], mode: "add" });
      toast.success("Crew added to booking");
      // Optimistic patch on the local row so the badge appears instantly
      setRows((prev) => prev.map((b) =>
        b.id === bookingId ? { ...b, assigned_user_ids: data.assigned_user_ids } : b));
    } catch (e) { toast.error(apiErrorMessage(e, "Could not assign crew")); }
    finally { setDragUser(null); setDropTarget(null); }
  };

  return (
    <div data-testid="sb-calendar">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {view === "month" ? (
          <>
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => move(-1)}
                    data-testid="sb-cal-prev">‹ Prev</Button>
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={jumpToday}
                    data-testid="sb-cal-today">Today</Button>
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => move(1)}
                    data-testid="sb-cal-next">Next ›</Button>
            <div className="text-lg font-bold ml-2 uppercase tracking-wide" data-testid="sb-cal-label">{monthLabel}</div>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => moveDay(-1)}
                    data-testid="sb-cal-day-prev">‹ Prev day</Button>
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={jumpTodayDay}
                    data-testid="sb-cal-day-today">Today</Button>
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => moveDay(1)}
                    data-testid="sb-cal-day-next">Next day ›</Button>
            <Input type="date" value={dayAnchor} onChange={(e) => setDayAnchor(e.target.value)}
                    className="rounded-none h-8 w-40" data-testid="sb-cal-day-picker" />
            <div className="text-base font-bold ml-2 tracking-wide" data-testid="sb-cal-day-label">{dayLabel}</div>
          </>
        )}
        {/* View toggle */}
        <div className="inline-flex border border-border ml-2" data-testid="sb-cal-view-toggle">
          {["month", "day"].map((v) => (
            <button key={v} onClick={() => setView(v)}
                     data-testid={`sb-cal-view-${v}`}
                     className={`px-2.5 py-1 text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                       view === v ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}>
              {v}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>pending</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>confirmed</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500"></span>in-progress</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400"></span>completed</span>
        </div>
      </div>
      {canManage && view === "month" && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {!wsIcsUrl ? (
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={mintWsIcs} disabled={wsIcsBusy}
                    data-testid="sb-cal-wsics-generate">
              <CalendarDays className="w-3.5 h-3.5 mr-1" />
              {wsIcsBusy ? "Generating…" : "Subscribe full calendar to Google/Apple"}
            </Button>
          ) : (
            <div className="flex items-center gap-1 border border-primary/40 bg-primary/5 px-2 py-1">
              <span className="text-[10px] font-mono truncate max-w-[380px]" title={wsIcsUrl}
                    data-testid="sb-cal-wsics-url">{wsIcsUrl}</span>
              <button onClick={copyWsIcs} className="text-primary hover:text-primary/80" title="Copy URL"
                      data-testid="sb-cal-wsics-copy">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button onClick={mintWsIcs} className="text-[10px] text-muted-foreground hover:text-foreground" title="Rotate feed URL"
                      data-testid="sb-cal-wsics-rotate" disabled={wsIcsBusy}>
                {wsIcsBusy ? "…" : "Rotate"}
              </button>
            </div>
          )}
          {wsIcsUrl && (
            <span className="text-[10px] text-muted-foreground max-w-lg">
              Paste this URL into Google Calendar (Other calendars → From URL) or Apple Calendar (File → New Calendar Subscription) so every shoot appears alongside personal events. Rotate to revoke.
            </span>
          )}
        </div>
      )}
      {view === "month" && (<>
      <div className="grid grid-cols-7 bg-muted/40 border border-border">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="px-2 py-1 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground text-center border-r border-border last:border-r-0">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 border-l border-b border-border">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} className="border-r border-t border-border min-h-[110px] bg-muted/20"></div>;
          const iso = isoDay(d);
          const list = (byDay[iso] || []).sort((a, b) => (a.slot_start || "").localeCompare(b.slot_start || ""));
          const isToday = iso === todayIso;
          return (
            <div key={iso}
                 className={`border-r border-t border-border min-h-[110px] p-1 flex flex-col gap-0.5 ${isToday ? "bg-primary/5" : "bg-white"}`}
                 data-testid={`sb-cal-day-${iso}`}
                 onClick={() => { setDayAnchor(iso); setView("day"); }}>
              <div className={`text-[10px] font-mono ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>{d}</div>
              {list.slice(0, 4).map((b) => (
                <button key={b.id} onClick={(e) => { e.stopPropagation(); setFocus(b); }}
                        data-testid={`sb-cal-booking-${b.id}`}
                        className="text-left text-[10px] border-l-2 pl-1 py-0.5 pr-1 hover:bg-primary/10 truncate flex items-center gap-1"
                        style={{ borderLeftColor: "currentColor" }}>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot(b.status)}`}></span>
                  <span className="font-mono flex-shrink-0">{b.slot_start?.slice(11, 16)}</span>
                  <span className="truncate">{b.customer_name || "—"}</span>
                </button>
              ))}
              {list.length > 4 && (
                <button onClick={(e) => { e.stopPropagation(); setFocus({ __day: iso, list }); }}
                        className="text-[10px] text-primary hover:underline text-left">
                  +{list.length - 4} more
                </button>
              )}
            </div>
          );
        })}
      </div>
      </>)}
      {view === "day" && (
        <DayViewGrid
          spaces={spaces} members={members}
          bookings={bookingsForDayView}
          halfHours={halfHours} totalSlots={totalSlots} ROW_PX={ROW_PX}
          DAY_START_HR={DAY_START_HR} SLOT_MINUTES={SLOT_MINUTES}
          statusDot={statusDot} slotIndex={slotIndex}
          canManage={canManage}
          dragUser={dragUser} setDragUser={setDragUser}
          dropTarget={dropTarget} setDropTarget={setDropTarget}
          onCrewDrop={onCrewDrop}
          onFocus={setFocus}
        />
      )}
      <CalendarFocusDialog booking={focus} clientName={clientName} memberName={memberName}
                             spaces={spaces} ws={ws}
                             onClose={() => setFocus(null)} />
    </div>
  );
}


// Feb 2026 · Day view — spaces × 30-min rail, absolute-positioned booking
// blocks. Left crew rail is draggable; drop onto a block to append crew.
function DayViewGrid({ spaces, members, bookings, halfHours, totalSlots, ROW_PX,
                        DAY_START_HR, SLOT_MINUTES, statusDot, slotIndex,
                        canManage, dragUser, setDragUser,
                        dropTarget, setDropTarget, onCrewDrop, onFocus }) {
  const activeSpaces = spaces.filter((s) => s.active !== false)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  // "Offsite" is a virtual column so we don't hide bookings without a space
  const columns = [
    ...activeSpaces,
    { id: "__offsite", name: "Offsite / other", virtual: true, color: "#94a3b8" },
  ];

  const findColumn = (b) => {
    if (b.is_offsite || !b.space_id) return "__offsite";
    return activeSpaces.some((s) => s.id === b.space_id) ? b.space_id : "__offsite";
  };
  const bookingsByColumn = {};
  for (const b of bookings) {
    const col = findColumn(b);
    (bookingsByColumn[col] = bookingsByColumn[col] || []).push(b);
  }

  const nowMins = (new Date()).getHours() * 60 + (new Date()).getMinutes();
  const nowSlot = Math.max(0, Math.min(totalSlots, (nowMins - DAY_START_HR * 60) / SLOT_MINUTES));

  return (
    <div className="flex gap-3" data-testid="sb-cal-day-view">
      {/* Crew sidebar */}
      {canManage && (
        <aside className="w-40 flex-shrink-0 border border-border bg-muted/30 p-2"
               data-testid="sb-cal-day-crew-rail">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
            Crew · drag to assign
          </div>
          <div className="space-y-1 max-h-[520px] overflow-y-auto">
            {members.length === 0 && (
              <div className="text-[10px] italic text-muted-foreground">Loading…</div>
            )}
            {members.map((m) => {
              const uid = m.user_id || m.id;
              const initials = (m.name || m.email || "?").slice(0, 2).toUpperCase();
              return (
                <div key={uid}
                     draggable
                     onDragStart={(e) => {
                       setDragUser(uid);
                       e.dataTransfer.setData("text/plain", uid);
                       e.dataTransfer.effectAllowed = "copy";
                     }}
                     onDragEnd={() => setDragUser(null)}
                     data-testid={`sb-cal-day-crew-${uid}`}
                     className={`text-[11px] px-2 py-1 border border-border bg-white cursor-grab hover:border-primary transition-colors flex items-center gap-2 ${dragUser === uid ? "opacity-40" : ""}`}
                     title="Drag onto a booking to add as crew">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[9px] font-bold uppercase inline-flex items-center justify-center">
                    {initials}
                  </span>
                  <span className="truncate">{m.name || m.email || uid.slice(0, 6)}</span>
                </div>
              );
            })}
          </div>
        </aside>
      )}
      {/* Grid */}
      <div className="flex-1 overflow-x-auto border border-border">
        <div className="flex min-w-[720px]">
          {/* Time gutter */}
          <div className="w-14 flex-shrink-0 bg-muted/40 border-r border-border">
            <div className="h-9 border-b border-border"></div>
            {halfHours.map((t, i) => (
              <div key={i} className="border-b border-border/70 text-right pr-1 text-[10px] font-mono text-muted-foreground"
                   style={{ height: ROW_PX }}>
                {t.m === 0 ? t.label : ""}
              </div>
            ))}
          </div>
          {/* Space columns */}
          {columns.map((col) => {
            const list = (bookingsByColumn[col.id] || []);
            return (
              <div key={col.id}
                   className="flex-1 min-w-[140px] border-r border-border last:border-r-0 relative"
                   data-testid={`sb-cal-day-col-${col.id}`}>
                <div className="h-9 px-2 py-1 border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-widest truncate flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: col.color || "#94a3b8" }}></span>
                  <span className="truncate">{col.name}</span>
                </div>
                <div className="relative" style={{ height: totalSlots * ROW_PX }}>
                  {/* 30-min grid lines */}
                  {halfHours.map((_, i) => (
                    <div key={i} className={`absolute left-0 right-0 ${i % 2 === 0 ? "border-t border-border/70" : "border-t border-dashed border-border/40"}`}
                         style={{ top: i * ROW_PX }}></div>
                  ))}
                  {/* Now-line if the day being viewed is today */}
                  {nowSlot > 0 && nowSlot < totalSlots && (
                    <div className="absolute left-0 right-0 z-10 pointer-events-none"
                         style={{ top: nowSlot * ROW_PX - 1, borderTop: "1.5px solid #EF4444" }}
                         data-testid={`sb-cal-day-now-${col.id}`}></div>
                  )}
                  {/* Booking blocks */}
                  {list.map((b) => {
                    const start = slotIndex(b.slot_start);
                    const end = slotIndex(b.slot_end);
                    const top = start * ROW_PX;
                    const height = Math.max(ROW_PX - 2, (end - start) * ROW_PX - 2);
                    const dotCls = statusDot(b.status);
                    const isDropTarget = dropTarget === b.id;
                    return (
                      <button key={b.id}
                              onClick={() => onFocus(b)}
                              onDragEnter={(e) => { e.preventDefault(); setDropTarget(b.id); }}
                              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
                              onDragLeave={() => setDropTarget((t) => t === b.id ? null : t)}
                              onDrop={(e) => {
                                e.preventDefault();
                                const uid = e.dataTransfer.getData("text/plain") || dragUser;
                                onCrewDrop(b.id, uid);
                              }}
                              data-testid={`sb-cal-day-booking-${b.id}`}
                              className={`absolute left-0.5 right-0.5 text-left overflow-hidden border-l-4 text-[10px] p-1 pr-1.5 bg-white hover:bg-primary/5 transition-colors ${isDropTarget ? "ring-2 ring-primary ring-inset shadow-md" : ""}`}
                              style={{ top: top + 1, height,
                                          borderLeftColor: b.status === "confirmed" ? "#10b981" :
                                                              b.status === "pending_payment" ? "#f59e0b" :
                                                              b.status === "in_progress" ? "#0ea5e9" :
                                                              b.status === "completed" ? "#94a3b8" :
                                                              b.status === "cancelled" ? "#f43f5e" : "#94a3b8" }}>
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotCls}`}></span>
                          <span className="font-mono text-[9px] flex-shrink-0">{b.slot_start?.slice(11, 16)}–{b.slot_end?.slice(11, 16)}</span>
                        </div>
                        <div className="font-semibold truncate">{b.customer_name || "—"}</div>
                        {b.code && <div className="text-[9px] font-mono text-muted-foreground truncate">{b.code}</div>}
                        {b.assigned_user_ids?.length > 0 && (
                          <div className="text-[9px] text-primary truncate mt-0.5">
                            👥 {b.assigned_user_ids.length}
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {list.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-[10px] text-muted-foreground/50 italic pointer-events-none">
                      No bookings
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CalendarFocusDialog({ booking, clientName, memberName, onClose }) {
  if (!booking) return null;
  // Multi-booking day (from "+N more" click)
  if (booking.__day) {
    return (
      <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="rounded-none max-w-lg max-h-[80vh] overflow-y-auto" data-testid="sb-cal-focus-day">
          <DialogHeader><DialogTitle>Bookings on {booking.__day}</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {booking.list.map((b) => (
              <div key={b.id} className="border border-border p-2 text-xs" data-testid={`sb-cal-focus-item-${b.id}`}>
                <div className="flex justify-between">
                  <div className="font-mono">{b.code}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{b.slot_start?.slice(11, 16)} – {b.slot_end?.slice(11, 16)}</div>
                </div>
                <div>{b.customer_name}</div>
                <div className="text-[10px] text-muted-foreground">{b.status}{b.is_offsite ? " · offsite" : ""}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  // Single-booking focus
  const b = booking;
  const cn = b.client_id ? clientName(b.client_id) : null;
  const crew = (b.assigned_user_ids || []).map((uid) => memberName(uid) || uid.slice(0, 6));
  const items = b.items || [];
  const eq = items.filter((it) => it.kind === "equipment");
  const cr = items.filter((it) => it.kind === "crew");
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="sb-cal-focus">
        <DialogHeader><DialogTitle>{b.code}</DialogTitle></DialogHeader>
        <div className="text-xs space-y-1.5">
          <div className="font-mono">{b.slot_start?.replace("T", " ").slice(0, 16)} → {b.slot_end?.replace("T", " ").slice(0, 16)}</div>
          <div><b>{b.customer_name}</b> {b.customer_email && <span className="text-muted-foreground">· {b.customer_email}</span>}</div>
          {cn && <div>Client: <span className="text-primary">{cn}</span></div>}
          <div>Status: <span className={`text-[10px] px-1.5 py-0.5 border ${statusColor(b.status)}`}>{b.status}</span></div>
          {b.is_offsite && <div className="text-amber-700">🚚 Offsite shoot</div>}
          {b.is_internal && <div className="text-amber-700">Internal booking (no payment)</div>}
          {eq.length > 0 && (
            <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">Equipment</div>
              {eq.map((e, i) => <div key={i}>· {e.name}</div>)}
            </div>
          )}
          {cr.length > 0 && (
            <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">Crew roles</div>
              {cr.map((c, i) => <div key={i}>· {c.name} × {c.count || 1}</div>)}
            </div>
          )}
          {crew.length > 0 && (
            <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">Assigned crew</div>
              <div className="flex flex-wrap gap-1">
                {crew.map((n, i) => <span key={i} className="text-[10px] bg-primary/10 text-primary px-1 py-0.5 border border-primary/30">{n}</span>)}
              </div>
            </div>
          )}
          {b.notes && <div className="text-muted-foreground italic border-t border-border pt-2 mt-2">{b.notes}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}



// Feb 2026 · Crew calendar / schedule view — 7-day grid, no financial info.
function CrewSchedule({ ws }) {
  const [rows, setRows] = useState([]);
  const [anchor, setAnchor] = useState(() => new Date().toISOString().slice(0, 10));
  const [icsUrl, setIcsUrl] = useState("");
  const [icsBusy, setIcsBusy] = useState(false);
  const load = async (startDate) => {
    const end = new Date(startDate); end.setDate(end.getDate() + 13);
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio/crew-bookings`,
        { params: { from_date: startDate, to_date: end.toISOString().slice(0, 10) } });
      setRows(data.bookings || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  const mintIcs = async () => {
    setIcsBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/my-ics-token`);
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setIcsUrl(abs);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not generate feed")); }
    finally { setIcsBusy(false); }
  };
  const copyIcs = async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      toast.success("iCal feed URL copied");
    } catch { toast.error("Copy failed"); }
  };
  useEffect(() => { if (ws) load(anchor); /* eslint-disable-next-line */ }, [ws, anchor]);
  const days = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date(anchor); d.setDate(d.getDate() + i);
    days.push(d.toISOString().slice(0, 10));
  }
  const byDay = {};
  for (const r of rows) {
    const k = r.slot_start?.slice(0, 10);
    if (!k) continue;
    byDay[k] = byDay[k] || [];
    byDay[k].push(r);
  }
  const move = (n) => {
    const d = new Date(anchor); d.setDate(d.getDate() + n);
    setAnchor(d.toISOString().slice(0, 10));
  };
  return (
    <div data-testid="sb-crew-schedule">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => move(-14)}>‹ Prev 2 weeks</Button>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setAnchor(new Date().toISOString().slice(0, 10))}>Today</Button>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => move(14)}>Next 2 weeks ›</Button>
        <div className="ml-auto flex items-center gap-1">
          {!icsUrl ? (
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={mintIcs} disabled={icsBusy}
                    data-testid="sb-crew-ical-generate">
              <CalendarDays className="w-3.5 h-3.5 mr-1" /> {icsBusy ? "Generating…" : "Subscribe to iCal"}
            </Button>
          ) : (
            <div className="flex items-center gap-1 border border-primary/40 bg-primary/5 px-2 py-1">
              <span className="text-[10px] font-mono truncate max-w-[260px]" title={icsUrl} data-testid="sb-crew-ical-url">
                {icsUrl}
              </span>
              <button onClick={copyIcs} className="text-primary hover:text-primary/80" title="Copy URL"
                      data-testid="sb-crew-ical-copy">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button onClick={mintIcs} className="text-[10px] text-muted-foreground hover:text-foreground" title="Rotate feed URL"
                      data-testid="sb-crew-ical-rotate" disabled={icsBusy}>
                {icsBusy ? "…" : "Rotate"}
              </button>
            </div>
          )}
        </div>
      </div>
      {icsUrl && (
        <div className="text-[10px] text-muted-foreground mb-2 max-w-2xl">
          Add this URL to Google Calendar (Other calendars → From URL) or Apple Calendar (File → New Calendar Subscription) to see your assigned shoots. Only bookings assigned to you appear. Rotate to revoke the link.
        </div>
      )}
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const dt = new Date(d + "T00:00:00");
          const isToday = d === new Date().toISOString().slice(0, 10);
          const bookings = byDay[d] || [];
          return (
            <div key={d} className={`border ${isToday ? "border-primary bg-primary/5" : "border-border"} p-2 min-h-[90px]`}
                  data-testid={`sb-crew-day-${d}`}>
              <div className={`text-[10px] font-semibold uppercase tracking-widest mb-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dt.getDay()]} {dt.getDate()}/{dt.getMonth() + 1}
              </div>
              {bookings.map((b) => (
                <div key={b.id} className="text-[10px] border-l-2 border-primary bg-white/80 px-1.5 py-1 mb-1"
                      title={`${b.customer_name} · ${b.slot_start?.slice(11, 16)} → ${b.slot_end?.slice(11, 16)}`}>
                  <div className="font-mono">{b.slot_start?.slice(11, 16)}–{b.slot_end?.slice(11, 16)}</div>
                  <div className="truncate">{b.customer_name}</div>
                  {b.is_internal && <span className="text-[8px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1">Internal</span>}
                </div>
              ))}
              {bookings.length === 0 && <div className="text-[10px] text-muted-foreground italic">—</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}



// Studio Analytics tab extracted to ./studioBooking/Analytics.jsx (Oct 2026).

// ─── Feb 2026 · R280 · Heatmap empty-cell time-picker popover ────────
// Renders an empty heatmap cell wrapped in a Popover with start + end
// time dropdowns (30-min ticks, 08:00 → 22:00). Submitting hands the
// window to the Manual Booking dialog for confirmation.
const _HM_TIME_TICKS = (() => {
  const out = [];
  for (let h = 8; h <= 22; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 22 && m > 0) break;
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();

function HeatmapEmptyCell({ space, date, onPick, title, cellColor }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState("10:00");
  const [end, setEnd]     = useState("14:00");
  const invalid = end <= start;
  const submit = () => {
    if (invalid) return;
    onPick(space, date, start, end);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <td role="button" tabIndex={0}
            className={`px-1 py-1 text-center font-mono cursor-pointer hover:ring-2 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary ${cellColor}`}
            title={title}
            data-testid={`sb-heatmap-cell-${space.id}-${date}`} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 rounded-none p-3" data-testid={`sb-heatmap-picker-${space.id}-${date}`}>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5 mb-1">
          <Clock className="w-3 h-3" /> Pick slot · {date}
        </div>
        <div className="text-[11px] font-semibold mb-2">{space.name}</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px]">Start</Label>
            <select value={start} onChange={(e) => setStart(e.target.value)}
                       data-testid={`sb-heatmap-picker-start-${space.id}-${date}`}
                       className="w-full h-8 text-xs font-mono border border-border rounded-none bg-white px-2">
              {_HM_TIME_TICKS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px]">End</Label>
            <select value={end} onChange={(e) => setEnd(e.target.value)}
                       data-testid={`sb-heatmap-picker-end-${space.id}-${date}`}
                       className="w-full h-8 text-xs font-mono border border-border rounded-none bg-white px-2">
              {_HM_TIME_TICKS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1 text-[10px] text-muted-foreground">
          <span>Duration: {invalid ? "—" : `${Math.round(((parseInt(end.slice(0,2),10)*60+parseInt(end.slice(3),10)) - (parseInt(start.slice(0,2),10)*60+parseInt(start.slice(3),10))) / 60 * 10) / 10}h`}</span>
          <div className="flex gap-1">
            {["1h", "2h", "4h", "8h"].map((preset) => {
              const hours = parseInt(preset, 10);
              return (
                <button key={preset} type="button"
                        onClick={() => {
                          const sMin = parseInt(start.slice(0,2),10) * 60 + parseInt(start.slice(3),10);
                          const eMin = sMin + hours * 60;
                          const eh = Math.floor(eMin / 60), em = eMin % 60;
                          setEnd(`${String(eh).padStart(2,"0")}:${String(em).padStart(2,"0")}`);
                        }}
                        className="px-1.5 py-0.5 border border-border hover:bg-primary/10 uppercase tracking-widest">
                  {preset}
                </button>
              );
            })}
          </div>
        </div>
        {invalid && <div className="text-[10px] text-rose-600 mt-1">End time must be after start.</div>}
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="rounded-none h-7 text-[11px] flex-1"
                    onClick={submit} disabled={invalid}
                    data-testid={`sb-heatmap-picker-confirm-${space.id}-${date}`}>
            Book this slot
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                    onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ─── Feb 2026 · R280 · Studio Emails · Admin settings tab ────────────
// Manages workspace-level:
//   • studio_admin_emails            (chips input)
//   • studio_email_templates.{kind}  (per-event editor with toggles)
//   • studio_daily_digest            (enable + recipients)
// Also exposes: preview, test-send-to-me, manual "send digest now".
// Studio email settings + ChipsEmailInput extracted to ./studioBooking/EmailSettings.jsx (Oct 2026).

// ─── Feb 2026 · R280.3 · One-tap accept for tracker reschedule ──────
// Renders under any thread comment where `meta.kind==="reschedule_request"`.
// Absent when the request has already been accepted or the current
// user lacks `studio.bookings.manage` (safeguards against staff
// mistake-clicks on kiosk devices).
// RescheduleAcceptFooter + BookingPaymentsLedger + RecordPaymentDialog extracted to ./studioBooking/Bookings.jsx (Oct 2026).
